/* ============================================================
   OPORA TOOLS — Wave 2 Additive Helpers (Q30 + Categories 2–8)
   Beta-Q build: 2026-06-19
   ============================================================

   Provides on window.OporaWire:
     F1: saveToLibrary(opts)         — POST /api/artifacts; anon nudge
     F2: copyShareLink(opts)         — POST /api/artifacts/:id/share → clipboard
     F3: handoffTo(slug,payload,opts) — POST /api/handoffs → navigate with context
     F4: openProfileDrawer()         — open <opora-profile> drawer
     F5: writeCalcOutput(key,val)    — persist calc results to localStorage
     F6: showShareModal(opts)        — Q30 expiry/watermark modal UI
     F7: recordGoalIncrement(opts)   — X1 dedup goal auto-increment
     F8: pushNotification(opts)      — X3 long-tool → notifications bell
     F9: trackToolError(opts)        — X4 every tool error → audit_log + Klaviyo
     F10: checkAccountTasks(accountId) — P5+P6 unified account_tasks lookup
     F11: wireAskHandoff(opts)       — A4 every tool → Ask Opora
     F12: applyPromptTemplate(opts)  — A3 AI Prompt Library → any tool
     F13: handleEngineHandoff(opts)  — A2 Engine → Bid/RFP/LostBid cite

   SPEC UPDATES APPLIED:
     W1  = manual button (not auto-trigger)
     A1  = function-calling structured JSON w/ chip fallback
     X1  = artifact_id dedup (goal_increments table)
     P5+P6 = unified account_tasks (one row, two views)
   ============================================================ */

(function (global) {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     INTERNAL: auth helpers
  ───────────────────────────────────────────────────────────── */
  async function _authHeaders() {
    if (global.OporaAccount && typeof global.OporaAccount.getAuthHeaders === 'function') {
      return global.OporaAccount.getAuthHeaders();
    }
    var token = global.__clerk_token || null;
    return token ? { 'Authorization': 'Bearer ' + token } : {};
  }

  function _apiFetch(path, opts) {
    opts = opts || {};
    var edge = global.OporaEdge || {};
    if (typeof edge.fetchApi === 'function') {
      return edge.fetchApi(path, opts);
    }
    var base = (global.OPORA_WORKER_URL || edge.WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
    var p = path.charAt(0) === '/' ? path : '/' + path;
    return fetch(base + p, opts);
  }

  function _isSignedIn() {
    if (global.OporaAccount && typeof global.OporaAccount.isSignedIn === 'function') {
      return global.OporaAccount.isSignedIn();
    }
    return !!(global.Clerk && global.Clerk.session);
  }

  function _signInUrl() {
    if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
      global.OporaAuth.openModal({ intent: 'sign-in' });
      return null;
    }
    var dash = (global.OporaEdge && global.OporaEdge.DASHBOARD_URL) || 'https://dashboard.oporasupply.com';
    return dash.replace(/\/$/, '') + '/';
  }

  /* ─────────────────────────────────────────────────────────────
     INTERNAL: toast
  ───────────────────────────────────────────────────────────── */
  function _showToast(msg, type) {
    var toast = document.createElement('div');
    toast.className = 'opora-toast opora-toast--' + (type || 'success');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('opora-toast--visible'); });
    setTimeout(function () {
      toast.classList.remove('opora-toast--visible');
      toast.classList.add('opora-toast--hiding');
      setTimeout(function () { toast.remove(); }, 300);
    }, 3000);
  }

  function _esc(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ─────────────────────────────────────────────────────────────
     F1: saveToLibrary(opts)
  ───────────────────────────────────────────────────────────── */
  async function saveToLibrary(opts) {
    if (!opts || !opts.tool || !opts.title || !opts.body_markdown) {
      console.warn('[OporaWire] saveToLibrary: tool, title, body_markdown required');
      return;
    }
    if (!_isSignedIn()) {
      try {
        sessionStorage.setItem('opora_pending_save_' + opts.tool,
          JSON.stringify({ tool: opts.tool, title: opts.title, body_markdown: opts.body_markdown, metadata: opts.metadata || {} }));
      } catch (e) {}
      if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function' && !global.OporaAuth.__oporaStub) {
        global.OporaAuth.openModal({
          intent: 'sign-up',
          inPlace: true,
          title: 'Save this result',
          tagline: 'Create a free account to edit, duplicate, and access this result across devices.',
          onAuthed: function () { saveToLibrary(opts); }
        });
        return;
      }
      if (global.OporaAccount && typeof global.OporaAccount.showSignInToSaveNudge === 'function') {
        global.OporaAccount.showSignInToSaveNudge({ toolSlug: opts.tool, containerId: opts.containerId || null });
      } else {
        _showToast('Sign in to save this to your library.', 'info');
      }
      return;
    }
    var btn = opts.buttonEl;
    var btnOrigText = '';
    if (btn) { btnOrigText = btn.textContent || ''; btn.disabled = true; btn.textContent = 'Saving…'; btn.setAttribute('aria-busy', 'true'); }
    try {
      var headers = await _authHeaders();
      headers['Content-Type'] = 'application/json';
      var apiBase = (global.OporaEdge && global.OporaEdge.WORKER_URL) || global.OPORA_WORKER_URL || 'https://api.oporasupply.com';
      apiBase = String(apiBase).replace(/\/$/, '');
      var saveBody = global.OporaAccountFile && global.OporaAccountFile.withAccountBody
        ? global.OporaAccountFile.withAccountBody({ tool: opts.tool, tool_slug: opts.tool, title: opts.title, body_markdown: opts.body_markdown, content: opts.body_markdown, metadata: opts.metadata || {} })
        : { tool: opts.tool, tool_slug: opts.tool, title: opts.title, body_markdown: opts.body_markdown, content: opts.body_markdown, metadata: opts.metadata || {} };
      var resp = await fetch(apiBase + '/api/artifacts', {
        method: 'POST', headers: headers,
        body: JSON.stringify(saveBody),
      });
      var data = await resp.json();
      if (!resp.ok || data.ok === false || data.error) {
        var errMsg = (data.error && (data.error.message || data.error)) || 'Could not save. Try again.';
        _showToast(errMsg, 'error');
        if (opts.onError) opts.onError(errMsg);
        return;
      }
      var artifactId = data.id || (data.data && data.data.id) || data.artifact_id || (data.data && data.data.artifact_id);
      _showToast('Saved to your library.', 'success');
      if (btn) { btn.textContent = 'Saved'; btn.disabled = true; btn.setAttribute('aria-label', 'Saved to library'); btn.classList.add('is-saved'); }
      if (opts.onSaved) opts.onSaved(artifactId);
      if (global.OporaAccountFile && typeof global.OporaAccountFile.setActiveFileId === 'function' && artifactId) {
        global.OporaAccountFile.setActiveFileId(artifactId);
      }
      // X1: record goal increment (dedup by artifact_id)
      if (artifactId) { recordGoalIncrement({ tool: opts.tool, artifactId: artifactId }); }
    } catch (err) {
      _showToast('Could not save. Check your connection and try again.', 'error');
      if (opts.onError) opts.onError(err.message || 'Network error');
    } finally {
      if (btn && btn.textContent === 'Saving…') { btn.disabled = false; btn.textContent = btnOrigText; btn.removeAttribute('aria-busy'); }
    }
  }

  function _checkPendingSave() {
    if (!_isSignedIn()) return;
    var slug = document.querySelector('meta[name="opora:tool-slug"]');
    if (!slug) return;
    var key = 'opora_pending_save_' + slug.content;
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return;
      sessionStorage.removeItem(key);
      var pending = JSON.parse(raw);
      if (pending && pending.tool && pending.title && pending.body_markdown) {
        saveToLibrary(Object.assign({}, pending, { onSaved: function (id) { _showToast('Saved — your result is in your library.', 'success'); } }));
      }
    } catch (e) {}
  }

  /* ─────────────────────────────────────────────────────────────
     F2: copyShareLink(opts) — Q30: create share, copy to clipboard
  ───────────────────────────────────────────────────────────── */
  async function copyShareLink(opts) {
    if (!opts || !opts.artifactId) { console.warn('[OporaWire] copyShareLink: artifactId required'); return; }
    var btn = opts.buttonEl;
    if (btn) { btn.disabled = true; btn.textContent = 'Creating link…'; }
    try {
      var headers = await _authHeaders();
      headers['Content-Type'] = 'application/json';
      var resp = await fetch('/api/artifacts/' + opts.artifactId + '/share', {
        method: 'POST', headers: headers,
        body: JSON.stringify({ expiry: opts.expiry || 'never', watermark: opts.watermark !== false }),
      });
      var data = await resp.json();
      if (!resp.ok || (!data.ok && !data.data)) {
        _showToast('Could not create share link. Try again.', 'error');
        return;
      }
      var shareData = data.data || data;
      var slug = shareData.slug || shareData.share_slug;
      var shareUrl = global.location.origin + '/pages/share?id=' + slug;
      await navigator.clipboard.writeText(shareUrl);
      _showToast('Share link copied!', 'success');
      if (btn) {
        btn.textContent = 'Copied!';
        btn.setAttribute('aria-label', 'Share link copied to clipboard');
        setTimeout(function () { btn.disabled = false; btn.textContent = opts.buttonLabel || 'Copy share link'; }, 2000);
      }
      return shareUrl;
    } catch (err) {
      _showToast('Could not copy. Clipboard access may be blocked.', 'error');
    } finally {
      if (btn && btn.textContent === 'Creating link…') { btn.disabled = false; btn.textContent = opts.buttonLabel || 'Copy share link'; }
    }
  }

  /* ─────────────────────────────────────────────────────────────
     F6: showShareModal(opts) — Q30 expiry picker + watermark toggle
     opts: { artifactId, artifactTitle, onShare(shareUrl) }
  ───────────────────────────────────────────────────────────── */
  function showShareModal(opts) {
    if (!opts || !opts.artifactId) return;
    // Remove any existing modal
    var existing = document.getElementById('opora-share-modal');
    if (existing) existing.remove();

    var modal = document.createElement('div');
    modal.id = 'opora-share-modal';
    modal.className = 'opora-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Share this result');
    modal.innerHTML =
      '<div class="opora-modal__overlay" data-close></div>' +
      '<div class="opora-modal__panel">' +
        '<div class="opora-modal__header">' +
          '<h2 class="opora-modal__title">Share this result</h2>' +
          '<button class="opora-modal__close" aria-label="Close share dialog" data-close>×</button>' +
        '</div>' +
        '<div class="opora-modal__body">' +
          '<label class="opora-modal__label" for="oms-expiry">Link expires</label>' +
          '<select id="oms-expiry" class="opora-modal__select">' +
            '<option value="never">Never</option>' +
            '<option value="7d">7 days</option>' +
            '<option value="30d">30 days</option>' +
            '<option value="24h">24 hours</option>' +
            '<option value="1h">1 hour</option>' +
          '</select>' +
          '<label class="opora-modal__checkbox-label">' +
            '<input type="checkbox" id="oms-watermark" checked class="opora-modal__checkbox"> ' +
            'Show "Generated with Opora" watermark' +
          '</label>' +
          '<div id="oms-result" class="opora-modal__result" style="display:none">' +
            '<input type="text" id="oms-url" class="opora-modal__url-input" readonly aria-label="Share URL">' +
            '<button id="oms-copy-btn" class="ot-btn ot-btn--sm">Copy link</button>' +
          '</div>' +
        '</div>' +
        '<div class="opora-modal__footer">' +
          '<button id="oms-create-btn" class="ot-btn ot-btn--primary">Create share link</button>' +
          '<button class="ot-btn ot-btn--ghost" data-close>Cancel</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(modal);

    // Focus trap helpers
    var panel = modal.querySelector('.opora-modal__panel');
    var createBtn = modal.querySelector('#oms-create-btn');
    var copyBtn = modal.querySelector('#oms-copy-btn');
    var closeButtons = modal.querySelectorAll('[data-close]');

    // Animate in
    requestAnimationFrame(function () { modal.classList.add('opora-modal--visible'); });

    function closeModal() {
      modal.classList.remove('opora-modal--visible');
      setTimeout(function () { modal.remove(); }, 200);
    }

    closeButtons.forEach(function (b) { b.addEventListener('click', closeModal); });

    createBtn.addEventListener('click', async function () {
      var expiry = modal.querySelector('#oms-expiry').value;
      var watermark = modal.querySelector('#oms-watermark').checked;
      createBtn.disabled = true;
      createBtn.textContent = 'Creating…';
      try {
        var headers = await _authHeaders();
        headers['Content-Type'] = 'application/json';
        var resp = await fetch('/api/artifacts/' + opts.artifactId + '/share', {
          method: 'POST', headers: headers,
          body: JSON.stringify({ expiry: expiry, watermark: watermark }),
        });
        var data = await resp.json();
        if (!resp.ok || (!data.ok && !data.data)) {
          _showToast('Could not create share link.', 'error');
          createBtn.disabled = false;
          createBtn.textContent = 'Create share link';
          return;
        }
        var shareData = data.data || data;
        var shareUrl = global.location.origin + '/pages/share?id=' + shareData.slug;
        var resultEl = modal.querySelector('#oms-result');
        var urlInput = modal.querySelector('#oms-url');
        urlInput.value = shareUrl;
        resultEl.style.display = '';
        createBtn.style.display = 'none';
        copyBtn.focus();
        if (opts.onShare) opts.onShare(shareUrl);
      } catch (e) {
        _showToast('Network error. Try again.', 'error');
        createBtn.disabled = false;
        createBtn.textContent = 'Create share link';
      }
    });

    if (copyBtn) {
      copyBtn.addEventListener('click', async function () {
        var url = modal.querySelector('#oms-url').value;
        try {
          await navigator.clipboard.writeText(url);
          copyBtn.textContent = 'Copied!';
          setTimeout(function () { copyBtn.textContent = 'Copy link'; }, 2000);
        } catch (e) {
          modal.querySelector('#oms-url').select();
          _showToast('Press Ctrl+C to copy.', 'info');
        }
      });
    }

    // Escape key close + focus first element
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeModal(); }
    });
    createBtn.focus();
  }

  /* ─────────────────────────────────────────────────────────────
     F3: handoffTo(targetSlug, payload, opts)
     Upgraded: POSTs to /api/handoffs for D1 persistence, then navigates.
  ───────────────────────────────────────────────────────────── */
  async function handoffTo(targetSlug, payload, opts) {
    if (!targetSlug) { console.warn('[OporaWire] handoffTo: targetSlug required'); return; }
    opts = opts || {};

    // Always stash payload so dest page can hydrate even if /api/handoffs fails
    try {
      if (payload && typeof payload === 'object') {
        sessionStorage.setItem('opora_prefill_' + targetSlug, JSON.stringify(payload));
      }
    } catch (eStash) {}

    // POST to /api/handoffs for persistence (fire-and-forget on failure)
    var contextToken = null;
    try {
      var body = {
        source_tool: opts.source_tool || _currentToolSlug(),
        dest_tool: targetSlug,
        wire_id: opts.wire_id || null,
        source_artifact_id: opts.source_artifact_id || null,
        account_id: (global.OporaAccountFile && global.OporaAccountFile.getId) ? global.OporaAccountFile.getId() : null,
        payload: payload || null,
        session_id: opts.session_id || null,
      };
      var resp = await _apiFetch('/api/handoffs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        var data = await resp.json();
        if (data.ok && data.data) {
          contextToken = data.data.context_token;
        }
      }
    } catch (e) {
      // Non-fatal: navigate anyway without context token
    }

    // Build destination URL — keep wage/state scalars on the query string as a
    // hard fallback when context/session hydration misses (Ask → Hire Right).
    var params = new URLSearchParams();
    if (contextToken) params.set('context', contextToken);
    var sourceSlug = opts.source_tool || _currentToolSlug();
    if (sourceSlug) params.set('from', sourceSlug);
    if (opts.source_artifact_id) params.set('from_item', opts.source_artifact_id);
    try {
      if (payload && typeof payload === 'object') {
        var wage = payload.base_wage || payload.wage || payload.wage_rate || payload.hourly_rate || payload.hourlyRate || payload.target_wage;
        if (wage != null && wage !== '' && Number(wage) > 0) {
          params.set('wage', String(wage));
          params.set('base_wage', String(wage));
          params.set('wage_rate', String(wage));
        }
        if (payload.state) params.set('state', String(payload.state));
        if (payload.role) params.set('role', String(payload.role));
        params.set('prefill', JSON.stringify(payload));
      }
    } catch (eQs) {}
    _appendPreviewThemeParams(params);
    global.location.href = _pageUrl(targetSlug, params);
  }

  function _currentToolSlug() {
    var meta = document.querySelector('meta[name="opora:tool-slug"]');
    return meta ? meta.content : '';
  }

  function _previewThemeId() {
    try {
      return new URLSearchParams(global.location.search).get('preview_theme_id');
    } catch (e) {
      return null;
    }
  }

  function _appendPreviewThemeParams(params) {
    var previewId = _previewThemeId();
    if (previewId && params) params.set('preview_theme_id', previewId);
    return params;
  }

  function _pageUrl(slug, params) {
    var qs = params && params.toString ? params.toString() : '';
    return '/pages/' + slug + (qs ? '?' + qs : '');
  }

  /* Wire [data-handoff-target] buttons */
  function wireHandoffButtons() {
    document.querySelectorAll('[data-handoff-target]').forEach(function (btn) {
      if (btn._opora_wired) return;
      btn._opora_wired = true;
      btn.addEventListener('click', function (e) {
        var target = btn.getAttribute('data-handoff-target');
        var artifactId = btn.getAttribute('data-artifact-id') || null;
        var sourceSlug = btn.getAttribute('data-source-slug') || _currentToolSlug();
        var wireId = btn.getAttribute('data-wire-id') || null;
        var payloadRaw = btn.getAttribute('data-payload') || null;
        var payload = null;
        if (payloadRaw) { try { payload = JSON.parse(payloadRaw); } catch (e) {} }
        handoffTo(target, payload, { source_tool: sourceSlug, wire_id: wireId, source_artifact_id: artifactId });
      });
    });
  }

  /* F3 support: show "Continuing from <source>" pill + fetch prefill payload */
  async function checkHandoffContext() {
    var params = new URLSearchParams(global.location.search);
    var contextToken = params.get('context');
    var fromSlug = params.get('from');
    if (!contextToken) return null;

    var TOOL_NAMES = {
      'rfp-decoder': 'RFP Decoder', 'rfp-filter': 'RFP Filter',
      'lost-bid-autopsy': 'Lost Bid Autopsy', 'bid-drafter': 'Bid Drafter',
      'walkthrough-voice': 'Walkthrough Voice', 'margin-sentinel': 'Margin Sentinel',
      'hire-right': 'Hire Right', 'handbook-drafter': 'Handbook Drafter',
      'customer-recovery': 'Customer Recovery', 'review-response': 'Review Response',
      'compliance-agent': 'Compliance Agent', 'bill-rate-calculator': 'Bill Rate Calc',
      'churn-cost-calculator': 'Churn Cost Calc', '1099-vs-w2-calculator': '1099 vs W-2',
      'ask-opora': 'Ask Opora', 'engine': 'Opora Engine', 'ai-prompt-library': 'Prompt Library',
      'scripts-library': 'Scripts Library', 'scope-of-work-generator': 'SOW Generator',
    };

    // Show pill immediately (UX doesn't wait for API)
    if (fromSlug) {
      var fromName = TOOL_NAMES[fromSlug] || fromSlug;
      _showHandoffPill(fromName, fromSlug);
    }

    // Fetch prefill payload from /api/handoffs?context=<token>
    var prefillPayload = null;
    try {
      var resp = await _apiFetch('/api/handoffs?context=' + encodeURIComponent(contextToken));
      if (resp.ok) {
        var data = await resp.json();
        if (data.ok && data.data && data.data.payload) {
          prefillPayload = data.data.payload;
          global._oporaHandoffPayload = prefillPayload;
          // Mark handoff complete
          if (data.data.handoff_id) {
            _apiFetch('/api/handoffs/' + data.data.handoff_id + '/complete', { method: 'POST' }).catch(function(){});
          }
        }
      }
    } catch (e) {}

    global._oporaHandoffArtifactId = contextToken;

    // Dispatch event so tool pages can react
    document.dispatchEvent(new CustomEvent('opora:handoff-ready', {
      detail: { contextToken: contextToken, fromSlug: fromSlug, payload: prefillPayload }
    }));

    return { contextToken: contextToken, fromSlug: fromSlug, payload: prefillPayload };
  }

  function _showHandoffPill(fromName, fromSlug) {
    var pill = document.createElement('div');
    pill.className = 'opora-handoff-pill';
    pill.setAttribute('role', 'status');
    var previewId = _previewThemeId();
    var backHref = '/pages/' + _esc(fromSlug);
    if (previewId) backHref += '?preview_theme_id=' + encodeURIComponent(previewId);
    pill.innerHTML =
      '<svg class="opora-handoff-pill__icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">' +
        '<path fill-rule="evenodd" d="M7.22 1.22a.75.75 0 011.06 0l3.25 3.25a.75.75 0 010 1.06L8.28 8.78a.75.75 0 01-1.06-1.06L9.44 5.5H1.75a.75.75 0 010-1.5h7.69L7.22 2.28a.75.75 0 010-1.06z" clip-rule="evenodd"/>' +
      '</svg>' +
      '<span class="opora-handoff-pill__text">Continuing from <strong>' + _esc(fromName) + '</strong></span>' +
      '<a href="' + backHref + '" class="opora-handoff-pill__back" aria-label="Go back to ' + _esc(fromName) + '">Back</a>';
    var target = document.querySelector('main') || document.querySelector('.tool-body') || document.body;
    target.insertAdjacentElement('afterbegin', pill);
    setTimeout(function () {
      document.querySelectorAll('[data-prefilled="true"]').forEach(function (el) {
        el.classList.add('opora-prefill-highlight');
        setTimeout(function () { el.classList.remove('opora-prefill-highlight'); }, 1800);
      });
    }, 400);
  }

  /* ─────────────────────────────────────────────────────────────
     F4: openProfileDrawer()
  ───────────────────────────────────────────────────────────── */
  function openProfileDrawer(triggerEl) {
    var drawer = document.querySelector('opora-profile');
    if (!drawer) { console.warn('[OporaWire] openProfileDrawer: <opora-profile> not in DOM'); return; }
    if (triggerEl) drawer._triggerEl = triggerEl;
    if (typeof drawer.open === 'function') { drawer.open(); } else { drawer.setAttribute('open', ''); }
  }

  /* ─────────────────────────────────────────────────────────────
     F5: writeCalcOutput / readCalcOutput / resolveCalcTemplate
  ───────────────────────────────────────────────────────────── */
  var CALC_STORAGE_KEY = 'OporaCalcOutputs';

  function writeCalcOutput(toolSlug, fields) {
    if (!toolSlug || typeof fields !== 'object') return;
    try {
      var existing = {};
      var raw = localStorage.getItem(CALC_STORAGE_KEY);
      if (raw) existing = JSON.parse(raw);
      existing[toolSlug] = Object.assign({}, existing[toolSlug] || {}, fields, { _updated_at: Date.now() });
      localStorage.setItem(CALC_STORAGE_KEY, JSON.stringify(existing));
      global.OporaCalcOutputs = existing;
    } catch (e) { console.warn('[OporaWire] writeCalcOutput: localStorage write failed'); }
  }

  function readCalcOutput(toolSlug) {
    try {
      var raw = localStorage.getItem(CALC_STORAGE_KEY);
      if (!raw) return null;
      var all = JSON.parse(raw);
      return toolSlug ? (all[toolSlug] || null) : all;
    } catch (e) { return null; }
  }

  function resolveCalcTemplate(text) {
    if (!text) return text;
    var allCalc = readCalcOutput(null) || {};
    return text.replace(/\{\{calc\.([a-z_]+)\.([a-z_]+)\}\}/g, function (match, slug, field) {
      var tool = allCalc[slug];
      if (!tool) return match;
      var val = tool[field];
      return (val === undefined || val === null) ? match : String(val);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     F7: recordGoalIncrement(opts) — X1 dedup by artifact_id
     Calls /api/goals/increment — worker checks goal_increments table
     to prevent double-counting. Fire-and-forget.
  ───────────────────────────────────────────────────────────── */
  function recordGoalIncrement(opts) {
    if (!opts || !opts.tool || !opts.artifactId) return;
    if (!_isSignedIn()) return;
    _authHeaders().then(function (headers) {
      headers['Content-Type'] = 'application/json';
      fetch('/api/goals/increment', {
        method: 'POST', headers: headers,
        body: JSON.stringify({ tool: opts.tool, artifact_id: opts.artifactId }),
      }).catch(function () {});
    });
  }

  /* ─────────────────────────────────────────────────────────────
     F8: pushNotification(opts) — X3 long-tool → notifications bell
     opts: { title, body, type? }
  ───────────────────────────────────────────────────────────── */
  function pushNotification(opts) {
    if (!opts || !opts.title) return;
    if (!_isSignedIn()) return;
    _authHeaders().then(function (headers) {
      headers['Content-Type'] = 'application/json';
      fetch('/api/notifications', {
        method: 'POST', headers: headers,
        body: JSON.stringify({ type: opts.type || 'tool_complete', title: opts.title, body: opts.body || '' }),
      }).catch(function () {});
    });
    // Also dispatch local event so bell badge can update without refresh
    document.dispatchEvent(new CustomEvent('opora:notification-new', { detail: opts }));
  }

  /* ─────────────────────────────────────────────────────────────
     F9: trackToolError(opts) — X4 error → audit_log + Klaviyo
     opts: { tool, error_code, message, req_id? }
     Called from every tool's catch block.
  ───────────────────────────────────────────────────────────── */
  function trackToolError(opts) {
    if (!opts || !opts.tool) return;
    // Skip auth/rate-limit errors (they have their own paths)
    if (opts.error_code === 'UNAUTHORIZED' || opts.error_code === 'RATE_LIMITED') return;
    // Fire-and-forget — never blocks UI
    _authHeaders().then(function (headers) {
      headers['Content-Type'] = 'application/json';
      fetch('/api/audit-log', {
        method: 'POST', headers: headers,
        body: JSON.stringify({
          event: 'tool_error', tool: opts.tool,
          error_code: opts.error_code || 'UNKNOWN',
          message: opts.message || '', req_id: opts.req_id || '',
        }),
      }).catch(function () {});
    });
  }

  /* ─────────────────────────────────────────────────────────────
     F10: checkAccountTasks(accountId) — P5+P6 unified tasks lookup
     Returns { recovery: task|null, review_response: task|null }
     Used by Customer Recovery + Review Response to show cross-task pills.
  ───────────────────────────────────────────────────────────── */
  async function checkAccountTasks(accountId) {
    if (!accountId || !_isSignedIn()) return { recovery: null, review_response: null };
    try {
      var headers = await _authHeaders();
      var resp = await fetch('/api/account-tasks?account_id=' + encodeURIComponent(accountId), { headers: headers });
      if (!resp.ok) return { recovery: null, review_response: null };
      var data = await resp.json();
      if (!data.ok) return { recovery: null, review_response: null };
      var tasks = data.data.tasks || [];
      var result = { recovery: null, review_response: null };
      tasks.forEach(function (t) {
        if (t.task_type === 'recovery' && t.status === 'pending') result.recovery = t;
        if (t.task_type === 'review_response' && t.status === 'pending') result.review_response = t;
      });
      return result;
    } catch (e) { return { recovery: null, review_response: null }; }
  }

  /* Show the cross-task pending pill (P5 or P6 depending on current tool) */
  function showPendingTaskPill(task, currentTool) {
    if (!task) return;
    var isRecovery = task.task_type === 'recovery';
    var otherTool = isRecovery ? 'customer-recovery' : 'review-response';
    var otherName = isRecovery ? 'Customer Recovery' : 'Review Response';
    var pill = document.createElement('div');
    pill.className = 'opora-handoff-pill opora-handoff-pill--task';
    pill.setAttribute('role', 'status');
    pill.innerHTML =
      '<span class="opora-handoff-pill__text">Pending task: <strong>' + _esc(otherName) + '</strong> for this account</span>' +
      '<a href="/pages/' + _esc(otherTool) + '" class="opora-handoff-pill__back">Open ' + _esc(otherName) + '</a>';
    var target = document.querySelector('main') || document.body;
    target.insertAdjacentElement('afterbegin', pill);
  }

  /* ─────────────────────────────────────────────────────────────
     F11: wireAskHandoff — REMOVED (Ask-about-this secondary CTA)
     Kept as no-op so older callers do not throw.
  ───────────────────────────────────────────────────────────── */
  function wireAskHandoff() { /* intentionally empty */ }

  function stripAskHandoffChrome(root) {
    var scope = root || document;
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(
      '.ot-ask-handoff-btn, .ot-ask-handoff-slot, [data-ask-handoff-target], .calc-d4-secondary-ask, .calc-d4-secondary-ask-link, .bd-edit-hint, #bdEditHint'
    ).forEach(function (el) {
      if (!el || !el.parentNode) return;
      if (el.closest && el.closest('.opora-edge-wc-band--ask-handoff')) return;
      el.parentNode.removeChild(el);
    });
  }

  /** Primary Dual-CTA targets for Edge tools (Liquid owns most; JS fills gaps). */
  var PRIMARY_HANDOFFS = {
    'walkthrough-voice': {
      hint: 'Next: lock the scope from this walkthrough',
      href: '/pages/scope-of-work-generator',
      label: 'Build a scope → SOW Generator'
    },
    'bid-drafter': {
      hint: 'Next: check if this price holds',
      href: '/pages/margin-sentinel',
      label: 'Check this margin → Margin Sentinel'
    },
    'rfp-decoder': {
      hint: 'Next: draft only if you are going',
      href: '/pages/bid-drafter',
      label: 'Draft a bid → Bid Drafter'
    },
    'margin-sentinel': {
      hint: 'Next: rebuild the proposal at a healthier price',
      href: '/pages/bid-drafter',
      label: 'Re-price the bid → Bid Drafter'
    },
    'scope-of-work-generator': {
      hint: 'Next: price this scope into a proposal',
      href: '/pages/bid-drafter',
      label: 'Draft a bid → Bid Drafter'
    },
    'customer-recovery': {
      hint: 'Next: rebuild a core-scope proposal at a lower total — not a cheaper rate on the same work',
      href: '/pages/bid-drafter',
      label: 'Re-quote with adjusted scope → Bid Drafter'
    },
    'review-response': {
      hint: 'Next: rebuild the account, not just the review',
      href: '/pages/customer-recovery',
      label: 'Build a recovery plan → Customer Recovery'
    },
    'lost-bid-autopsy': {
      hint: 'Next: re-draft with the gaps closed',
      href: '/pages/bid-drafter',
      label: 'Re-draft the bid → Bid Drafter'
    },
    'hire-right': {
      hint: 'Step 2: Verify independent contractor risk before posting',
      href: '/pages/1099-vs-w2-calculator',
      label: 'Audit worker classification → IC vs W-2 Inspector',
      secondary: {
        href: '/pages/bill-rate-calculator',
        label: 'Calculate loaded labor → Bill Rate'
      }
    },
    'handbook-drafter': {
      hint: 'Next: export PDF or save for counsel review before distributing to new hires',
      href: '#opDocBody',
      label: 'Review draft above · Export PDF'
    },
    '1099-vs-w2-calculator': {
      hint: 'Next: hire against the classification you just confirmed',
      href: '/pages/hire-right',
      label: 'Screen the hire → Hire Right'
    },
    '1099-vs-w2': {
      hint: 'Next: hire against the classification you just confirmed',
      href: '/pages/hire-right',
      label: 'Screen the hire → Hire Right'
    },
    'compliance-agent': {
      hint: 'Next: put the fixes in writing',
      href: '/pages/handbook-drafter',
      label: 'Draft a handbook → Handbook Drafter'
    },
    'ask-opora': {
      hint: 'Next: apply this wage in a proposal',
      href: '/pages/bid-drafter',
      label: 'Draft proposal with this wage → Bid Drafter',
      secondary: {
        href: '/pages/margin-sentinel',
        label: 'Calculate fully loaded bill rate → Margin Sentinel'
      }
    },
    'scripts-library': {
      hint: 'Quick Starters now live inside Ask',
      href: '/pages/ask-opora?starters=1',
      label: 'Open Quick Starters → Ask Opora'
    }
  };

  function ensurePrimaryHandoff(panel, slug) {
    if (!panel || !slug) return;
    /* Handbook: actions live in document toolbar — do not inject Compliance Agent loop */
    if (slug === 'handbook-drafter') return;
    var spec = PRIMARY_HANDOFFS[slug];
    if (!spec) return;
    /* Hire Right: always remount Dual-CTA — never leave Step 2 empty if Liquid/chrome drifted */
    if (slug === 'hire-right') {
      remountHireRightHandoffs(panel, spec);
      return;
    }
    if (panel.querySelector('.calc-d4-primary-cta')) return;
    var band = panel.querySelector('.opora-edge-wc-band, .d4-sow-wc-band, .sow-wc-band');
    if (!band) {
      band = document.createElement('div');
      band.className = 'opora-edge-wc-band';
      band.style.padding = '0 22px 22px';
      panel.appendChild(band);
    }
    var handoff = document.createElement('div');
    handoff.className = 'calc-d4-primary-handoff';
    handoff.innerHTML =
      '<p class="calc-d4-primary-hint">' + _esc(spec.hint) + '</p>' +
      '<a class="calc-d4-primary-cta" href="' + _esc(spec.href) + '">' + _esc(spec.label) + '</a>' +
      (spec.secondary
        ? '<a class="calc-d4-primary-secondary" href="' + _esc(spec.secondary.href) + '">' + _esc(spec.secondary.label) + '</a>'
        : '');
    band.appendChild(handoff);
  }

  function remountHireRightHandoffs(panel, spec) {
    spec = spec || PRIMARY_HANDOFFS['hire-right'];
    if (!panel || !spec) return;
    var band = panel.querySelector('.opora-edge-wc-band, .d4-sow-wc-band, .sow-wc-band');
    if (!band) {
      band = document.createElement('div');
      band.className = 'opora-edge-wc-band';
      band.style.padding = '0 22px 22px';
      panel.appendChild(band);
    }
    var wrap = band.querySelector('[data-hire-handoffs], .calc-d4-primary-handoff');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'calc-d4-primary-handoff';
      wrap.setAttribute('data-hire-handoffs', '');
      band.appendChild(wrap);
    }
    wrap.setAttribute('data-hire-handoffs', '');
    wrap.innerHTML =
      '<p class="calc-d4-primary-hint">' + _esc(spec.hint) + '</p>' +
      '<a class="calc-d4-primary-cta" href="' + _esc(spec.href) + '" data-hire-handoff="1099">' + _esc(spec.label) + '</a>' +
      (spec.secondary
        ? '<a class="calc-d4-primary-secondary" href="' + _esc(spec.secondary.href) + '" data-hire-handoff="bill-rate">' + _esc(spec.secondary.label) + '</a>'
        : '');
  }

  /* ─────────────────────────────────────────────────────────────
     F12: applyPromptTemplate(opts) — A3 AI Prompt Library → any tool
     opts: { templateText, targetFieldSelector, toolSlug }
     Deep-links from prompt library into a tool's input field.
  ───────────────────────────────────────────────────────────── */
  function applyPromptTemplate(opts) {
    if (!opts || !opts.templateText) return;
    // Resolve {{calc.*}} tokens first
    var resolved = resolveCalcTemplate(opts.templateText);
    // If a selector is provided (same-page), fill directly
    if (opts.targetFieldSelector) {
      var field = document.querySelector(opts.targetFieldSelector);
      if (field) {
        if (field.tagName === 'TEXTAREA' || field.tagName === 'INPUT') {
          field.value = resolved;
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.setAttribute('data-prefilled', 'true');
          _showToast('Template applied.', 'success');
        }
        return;
      }
    }
    // Cross-page: store in sessionStorage and navigate to tool
    if (opts.toolSlug) {
      try {
        sessionStorage.setItem('opora_prompt_template_' + opts.toolSlug, resolved);
      } catch (e) {}
      handoffTo(opts.toolSlug, { prefill_text: resolved }, { source_tool: 'ai-prompt-library', wire_id: 'A3' });
    }
  }

  /* On DOMReady: check if a prompt template was stored for this tool */
  function _checkPendingTemplate() {
    var meta = document.querySelector('meta[name="opora:tool-slug"]');
    if (!meta) return;
    var key = 'opora_prompt_template_' + meta.content;
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return;
      sessionStorage.removeItem(key);
      // Find the first input/textarea and fill it
      var field = document.querySelector('textarea[data-main-input], input[data-main-input]') ||
                  document.querySelector('.ot-input-section textarea, .ot-input-section input');
      if (field) {
        field.value = raw;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.setAttribute('data-prefilled', 'true');
        _showToast('Prompt template applied.', 'success');
      }
    } catch (e) {}
  }

  /* ─────────────────────────────────────────────────────────────
     F13: handleEngineHandoff(opts) — A2 Engine → Bid/RFP/LostBid
     Takes research output and creates a handoff with citations as context.
     opts: { citations, summary, targetTool, artifactId }
  ───────────────────────────────────────────────────────────── */
  function handleEngineHandoff(opts) {
    if (!opts || !opts.targetTool) return;
    var payload = {
      engine_summary: opts.summary || '',
      engine_citations: opts.citations || [],
      source_artifact_id: opts.artifactId || null,
    };
    handoffTo(opts.targetTool, payload, {
      source_tool: 'engine',
      wire_id: 'A2',
      source_artifact_id: opts.artifactId || null,
    });
  }

  /* ─────────────────────────────────────────────────────────────
     CATEGORY 2 WIRE HELPERS (W1-W5, W7-W10, W12)
     Each function renders a CTA button in the source tool result.
  ───────────────────────────────────────────────────────────── */

  /* W1: RFP Decoder → Compliance Agent (MANUAL BUTTON — spec update applied) */
  function wireW1_RFPToCompliance(resultContainer, rfpData) {
    if (!resultContainer) return;
    var existing = resultContainer.querySelector('[data-wire="W1"]');
    if (existing) return;
    var btn = document.createElement('button');
    btn.className = 'ot-btn ot-btn--secondary ot-wire-btn';
    btn.setAttribute('data-wire', 'W1');
    btn.textContent = 'Run compliance check on this RFP';
    btn.setAttribute('aria-label', 'Run a compliance check based on this RFP');
    var resultArea = document.createElement('div');
    resultArea.className = 'ot-wire-inline-result';
    resultArea.style.display = 'none';
    btn.addEventListener('click', async function () {
      btn.disabled = true;
      btn.textContent = 'Checking…';
      var payload = rfpData || _readDataAttribute(resultContainer, 'rfp-data');
      try {
        // Navigate with handoff (user lands on compliance-agent with prefill)
        await handoffTo('compliance-agent', payload, { source_tool: 'rfp-decoder', wire_id: 'W1' });
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Run compliance check on this RFP';
        _showToast('Could not open Compliance Agent.', 'error');
      }
    });
    resultContainer.appendChild(btn);
    resultContainer.appendChild(resultArea);
  }

  /* W2: RFP Decoder → Walkthrough Voice */
  function wireW2_RFPToWalkthrough(resultContainer, rfpData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W2"]')) return;
    var btn = _makeHandoffButton('Schedule site walk', 'W2', 'walkthrough-voice', rfpData || {}, 'rfp-decoder');
    resultContainer.appendChild(btn);
  }

  /* W3: RFP Decoder → Bid Drafter */
  function wireW3_RFPToBidDrafter(resultContainer, rfpData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W3"]')) return;
    var btn = _makeHandoffButton('Reply with Bid Drafter →', 'W3', 'bid-drafter', rfpData || {}, 'rfp-decoder', { primary: true });
    resultContainer.appendChild(btn);
  }

  /* W4: Walkthrough Voice → SOW */
  function wireW4_WalkthroughToSOW(resultContainer, walkthroughData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W4"]')) return;
    var btn = _makeHandoffButton('Build SOW from this walkthrough', 'W4', 'scope-of-work-generator', walkthroughData || {}, 'walkthrough-voice');
    resultContainer.appendChild(btn);
  }

  /* W5: Walkthrough Voice → Bid Drafter */
  function wireW5_WalkthroughToBidDrafter(resultContainer, walkthroughData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W5"]')) return;
    var btn = _makeHandoffButton('Push to Bid Drafter →', 'W5', 'bid-drafter', walkthroughData || {}, 'walkthrough-voice', { primary: true });
    resultContainer.appendChild(btn);
  }

  /* W7: Compliance Agent → Bid Drafter (exclusions) */
  function wireW7_ComplianceToBidDrafter(resultContainer, complianceData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W7"]')) return;
    var btn = _makeHandoffButton('Push to Bid Drafter →', 'W7', 'bid-drafter', complianceData || {}, 'compliance-agent', { primary: true });
    resultContainer.appendChild(btn);
  }

  /* W8: Bill Rate Calc → Bid Drafter */
  function wireW8_BillRateToBidDrafter(resultContainer, rateData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W8"]')) return;
    var btn = _makeHandoffButton('Use these rates in Bid Drafter', 'W8', 'bid-drafter', rateData || {}, 'bill-rate-calculator');
    resultContainer.appendChild(btn);
  }

  /* W6: SOW Generator → Bid Drafter */
  function wireW6_SOWToBidDrafter(resultContainer, sowData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W6"]')) return;
    var payload = {
      prefill_text: (sowData && (sowData.scope_markdown || sowData.sow_markdown || sowData.proposal_markdown)) || '',
      scope_summary: (sowData && sowData.summary) || ''
    };
    resultContainer.appendChild(_makeHandoffButton(
      'Open Bid Drafter with this SOW →',
      'W6', 'bid-drafter', payload, 'scope-of-work-generator', { primary: true }
    ));
  }

  /* W9: Bid Drafter → Margin Sentinel (pre-submit gate) */
  function wireW9_BidToMarginSentinel(resultContainer, bidData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W9"]')) return;
    var btn = _makeHandoffButton('Validate with Margin Sentinel →', 'W9', 'margin-sentinel', bidData || {}, 'bid-drafter', { primary: true });
    btn.classList.add('ot-btn--warning');
    resultContainer.appendChild(btn);
  }

  /* W10: Bid Drafter → Bid Performance Tracker (on submit) */
  function wireW10_BidToBidTracker(bidData) {
    if (!bidData) return;
    if (!_isSignedIn()) return;
    _authHeaders().then(function (headers) {
      headers['Content-Type'] = 'application/json';
      fetch('/api/bid-tracker/record', {
        method: 'POST', headers: headers,
        body: JSON.stringify({
          solicitation_id: bidData.solicitation_id || '',
          agency: bidData.agency || '',
          naics: bidData.naics || '',
          submitted_price: bidData.submitted_price || 0,
          status: 'submitted',
        }),
      }).catch(function () {});
    });
  }

  /* W12: RFP Filter → Bid Drafter */
  function wireW12_RFPFilterToBidDrafter(resultContainer, rfpItem) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="W12"]')) return;
    var btn = _makeHandoffButton('Draft bid for this RFP', 'W12', 'bid-drafter', rfpItem || {}, 'rfp-filter');
    resultContainer.appendChild(btn);
  }

  /* ─────────────────────────────────────────────────────────────
     CATEGORY 3 WIRE HELPERS (P1, P3, P5+P6, P7)
  ───────────────────────────────────────────────────────────── */

  /* P1: Margin Sentinel → Customer Recovery */
  function wireP1_MarginToRecovery(resultContainer, marginData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="P1"]')) return;
    var accounts = (marginData && marginData.at_risk_accounts) || [];
    var blob = JSON.stringify(marginData || {});
    var risky = accounts.length > 0 || /under\s+benchmark|below\s+benchmark|underpriced|margin\s+risk|below\s+floor/i.test(blob);
    var label = accounts.length
      ? accounts.length + ' account' + (accounts.length === 1 ? '' : 's') + ' below margin — open recovery →'
      : (risky ? 'Open Customer Recovery →' : 'Draft recovery outreach (optional) →');
    var btn = _makeHandoffButton(label, 'P1', 'customer-recovery',
      { at_risk_accounts: accounts, situation: risky ? 'Margin Sentinel flagged risk' : 'Post-margin follow-up', prefill_text: blob.slice(0, 2000) },
      'margin-sentinel', { primary: risky });
    if (risky) btn.classList.add('ot-btn--warning');
    resultContainer.appendChild(btn);
  }

  /* P3: Churn Cost Calc → Customer Recovery */
  function wireP3_ChurnToRecovery(resultContainer, churnData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="P3"]')) return;
    var topAccounts = (churnData && churnData.top_risk_accounts) || [];
    if (topAccounts.length === 0) return;
    var btn = _makeHandoffButton(
      'Open recovery playbook for top-risk accounts',
      'P3', 'customer-recovery',
      { priority_accounts: topAccounts, total_at_risk: churnData.total_churn_cost || 0 },
      'churn-cost-calculator'
    );
    btn.classList.add('ot-btn--danger');
    resultContainer.appendChild(btn);
  }

  /* P5+P6: Unified account_tasks — show cross-task pill (bidirectional) */
  async function wireP5P6_AccountTaskBridge(resultContainer, accountId, currentTool) {
    if (!resultContainer || !accountId) return;
    var tasks = await checkAccountTasks(accountId);
    var pendingTask = currentTool === 'customer-recovery' ? tasks.review_response : tasks.recovery;
    if (pendingTask) { showPendingTaskPill(pendingTask, currentTool); }
  }

  function crFieldVal(id) {
    var el = document.getElementById(id);
    return el && el.value != null ? String(el.value).trim() : '';
  }
  function crMonthly() {
    var n = Number(crFieldVal('opf_cr-value'));
    return n > 0 ? n : 0;
  }
  function crScaledMonthly() {
    return Math.round(crMonthly() * 0.85);
  }
  function crMsVertical(building) {
    if (building === 'commercial') return 'office';
    return building || 'office';
  }
  function crBidDrafterPayload() {
    var monthly = crMonthly();
    var scaled = crScaledMonthly();
    var company = crFieldVal('opf_cr-company');
    var building = crFieldVal('opf_cr-building') || 'commercial';
    var notes = 'Core-scope re-quote from Customer Recovery. Current contract $' +
      monthly.toLocaleString() + '/mo. Target a 15% scope reduction to $' +
      scaled.toLocaleString() + '/mo. Strip extras (floor program, extra restroom frequency, day porter). Do not cut the loaded rate on the same work.';
    return {
      company_name: company,
      'opf_bd-company': company,
      building_type: building,
      'opf_bd-building': building,
      pricing_mode: 'target_monthly',
      'opf_bd-pricing-mode': 'target_monthly',
      target_monthly: scaled,
      'opf_bd-target-monthly': String(scaled),
      'opf_bd-notes': notes
    };
  }
  function crMarginPayload() {
    var scaled = crScaledMonthly();
    var building = crFieldVal('opf_cr-building') || 'commercial';
    return {
      bid_price_total: scaled,
      'opf_bid_price_total': String(scaled),
      'opf_vertical': crMsVertical(building)
    };
  }
  function stashPrefill(slug, payload) {
    try { sessionStorage.setItem('opora_prefill_' + slug, JSON.stringify(payload || {})); } catch (e) {}
  }
  function bindCrHandoffs(panel) {
    if (!panel) return;
    function bind(el, slug, payloadFn, wireId) {
      if (!el || el.getAttribute('data-cr-bound') === '1') return;
      el.setAttribute('data-cr-bound', '1');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var payload = payloadFn();
        stashPrefill(slug, payload);
        handoffTo(slug, payload, { source_tool: 'customer-recovery', wire_id: wireId });
      });
    }
    bind(
      panel.querySelector('[data-cr-handoff="bid-drafter"]') || panel.querySelector('.calc-d4-primary-cta'),
      'bid-drafter',
      crBidDrafterPayload,
      'P7'
    );
    bind(
      panel.querySelector('[data-cr-handoff="margin-sentinel"]') || panel.querySelector('.calc-d4-primary-secondary'),
      'margin-sentinel',
      crMarginPayload,
      'P7b'
    );
  }

  /* P7: Customer Recovery → Bid Drafter */
  function wireP7_RecoveryToBidDrafter(resultContainer, recoveryData) {
    bindCrHandoffs(resolveResultPanel(resultContainer) || resultContainer);
  }

  /* ─────────────────────────────────────────────────────────────
     CATEGORY 4 WIRE HELPERS (H1, H2)
  ───────────────────────────────────────────────────────────── */

  /* H1: Hire-Right → 1099 vs W-2 */
  function wireH1_HireRightTo1099(resultContainer, hireData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="H1"]')) return;
    var btn = _makeHandoffButton('Confirm classification with 1099 vs W-2 test', 'H1', '1099-vs-w2-calculator',
      { role_type: (hireData && hireData.recommended_classification) || '', state: (hireData && hireData.state) || '' },
      'hire-right');
    resultContainer.appendChild(btn);
  }

  /* H2: Hire-Right → Bill Rate Calc */
  function wireH2_HireRightToBillRate(resultContainer, hireData) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="H2"]')) return;
    var btn = _makeHandoffButton('Calculate loaded labor cost for this hire', 'H2', 'bill-rate-calculator',
      { classification: (hireData && hireData.recommended_classification) || '', state: (hireData && hireData.state) || '', role: (hireData && hireData.role_title) || '' },
      'hire-right');
    resultContainer.appendChild(btn);
  }

  function bindHireRightHandoffs(panel, hireData) {
    if (!panel) return;
    remountHireRightHandoffs(panel, PRIMARY_HANDOFFS['hire-right']);
    hireData = hireData || {};
    var ca = hireData.compensation_analysis || {};
    var state = String(hireData.state || (document.getElementById('opf_state') || {}).value || '').trim().toUpperCase();
    var wageLow = Number(ca.range_low);
    var wageHigh = Number(ca.range_high);
    var wage = ca.range_mid || ((wageLow > 0 && wageHigh > 0) ? ((wageLow + wageHigh) / 2) : '') || '';
    if (wage) wage = (Math.round(Number(wage) * 100) / 100).toFixed(2);
    var wageMin = wageLow > 0 ? (Math.round(wageLow * 100) / 100).toFixed(2) : '';
    var wageMax = wageHigh > 0 ? (Math.round(wageHigh * 100) / 100).toFixed(2) : '';
    var role = String(hireData.role_title || hireData.role || (document.getElementById('opf_role') || {}).value || 'cleaner').trim();
    var specialty = String(hireData.vertical || (document.getElementById('opf_vertical') || {}).value || 'commercial').trim().toLowerCase();
    var company = String(
      hireData.company || hireData.brand_name ||
      (hireData.business_profile && hireData.business_profile.brand_name) ||
      (document.getElementById('opf_brand') || {}).value || ''
    ).trim();
    var classif = '';
    if (hireData.classification_recommendation && hireData.classification_recommendation.w2_or_1099) {
      classif = String(hireData.classification_recommendation.w2_or_1099);
    }

    var cta = panel.querySelector('[data-hire-handoff="1099"]:not(.ot-classif-badge)') || panel.querySelector('.calc-d4-primary-cta');
    var sec = panel.querySelector('[data-hire-handoff="bill-rate"]') || panel.querySelector('.calc-d4-primary-secondary');
    var classifBadge = panel.querySelector('a.ot-classif-badge[data-hire-handoff="1099"]');
    var teamSize = String((document.getElementById('opf_company_size') || {}).value || '').trim();
    function hireUrlNum(n) {
      if (!(Number(n) > 0)) return '';
      var r = Math.round(Number(n) * 100) / 100;
      return Math.abs(r - Math.round(r)) < 1e-6 ? String(Math.round(r)) : r.toFixed(2);
    }
    function hireTeamMin(v) {
      v = String(v || '');
      if (v === '201+' || v === '200+' || v === '50+') return 201;
      if (v === '51-200') return 51;
      if (v === '21-50') return 21;
      if (v === '6-20') return 6;
      if (v === '2-5') return 2;
      if (v === '1-5' || v === '1') return 1;
      var m = v.match(/(\d+)/);
      return m ? Number(m[1]) : 0;
    }
    var teamMin = hireTeamMin(teamSize);
    var baseMin = hireUrlNum(wageLow);
    var baseMax = hireUrlNum(wageHigh);
    var loadedMin = hireUrlNum(Math.round(Number(ca.loaded_low) || 0));
    var loadedMax = hireUrlNum(Math.round(Number(ca.loaded_high) || 0));
    var everify = '';
    if (state === 'FL' && teamMin >= 21) everify = 'mandatory';
    else if (['AL', 'AZ', 'GA', 'MS', 'NC', 'SC', 'TN', 'UT'].indexOf(state) >= 0) everify = 'mandatory';
    if (cta) {
      cta.textContent = 'Audit worker classification → IC vs W-2 Inspector';
      var q1099 = new URLSearchParams();
      q1099.set('from', 'hire-right');
      if (state) q1099.set('state', state);
      if (wage) q1099.set('base_wage', wage);
      else if (baseMin) q1099.set('base_wage', baseMin);
      _appendPreviewThemeParams(q1099);
      var href1099 = '/pages/1099-vs-w2-calculator?' + q1099.toString();
      cta.setAttribute('href', href1099);
      if (classifBadge) classifBadge.setAttribute('href', href1099);
    }
    if (sec) {
      sec.textContent = 'Calculate loaded labor → Bill Rate';
      var qBr = new URLSearchParams();
      if (wage) qBr.set('wage', wage);
      if (wageMin) qBr.set('wage_min', wageMin);
      if (wageMax) qBr.set('wage_max', wageMax);
      if (state) qBr.set('state', state);
      if (role) qBr.set('role', role);
      if (specialty) qBr.set('specialty', specialty);
      if (company) qBr.set('company', company);
      _appendPreviewThemeParams(qBr);
      sec.setAttribute('href', '/pages/bill-rate-calculator' + (qBr.toString() ? '?' + qBr.toString() : ''));
    }
    try {
      sessionStorage.setItem('opora_prefill_bill-rate-calculator', JSON.stringify({
        wage_rate: wage, state: state, vertical: specialty, role: role, company: company,
        wage_min: wageMin, wage_max: wageMax
      }));
      sessionStorage.setItem('opora_prefill_1099-vs-w2-calculator', JSON.stringify({
        state: state,
        role: role,
        classification: classif,
        company: company,
        base_wage: wage || baseMin || '',
        wage_min: wageMin,
        wage_max: wageMax,
        contractor_rate: wage
          ? (Math.round(Number(wage) * 1.25 * 100) / 100).toFixed(2)
          : (baseMin ? (Math.round(Number(baseMin) * 1.25 * 100) / 100).toFixed(2) : ''),
        specialty: specialty,
        team_size: teamSize,
        from: 'hire-right',
        source_tool: 'hire-right'
      }));
      sessionStorage.setItem('opora_autorun_1099-vs-w2-calculator', '1');
    } catch (ePref) {}
  }

  /* ─────────────────────────────────────────────────────────────
     SHARED: _makeHandoffButton(label, wireId, targetSlug, payload, sourceSlug)
  ───────────────────────────────────────────────────────────── */
  function _makeHandoffButton(label, wireId, targetSlug, payload, sourceSlug, opts) {
    opts = opts || {};
    var btn = document.createElement('button');
    btn.className = 'ot-btn ot-btn--secondary ot-wire-btn';
    if (opts.primary) {
      btn.className = 'ot-btn ot-btn--primary ot-wire-btn ot-wire-btn--primary';
    }
    btn.setAttribute('data-wire', wireId);
    btn.setAttribute('data-handoff-target', targetSlug);
    btn.setAttribute('data-source-slug', sourceSlug || _currentToolSlug());
    btn.setAttribute('data-payload', JSON.stringify(payload || {}));
    btn.setAttribute('aria-label', label);
    btn.textContent = label;
    btn.addEventListener('click', function () {
      handoffTo(targetSlug, payload, { source_tool: sourceSlug || _currentToolSlug(), wire_id: wireId });
    });
    return btn;
  }

  var WORKFLOW_BID_CHAIN = {
    'rfp-decoder':       { path: 'RFP Decoder → Bid Drafter → Margin Sentinel', primaryWire: 'W3' },
    'walkthrough-voice': { path: 'Walkthrough → Bid Drafter → Margin Sentinel', primaryWire: 'W5' },
    'bid-drafter':       { path: 'Bid Drafter → Margin Sentinel → outcome', primaryWire: 'W9' },
    'compliance-agent':  { path: 'Compliance → Bid Drafter → Margin Sentinel', primaryWire: 'W7' },
    'margin-sentinel':   { path: 'Margin Sentinel → Customer Recovery (if at risk)', primaryWire: 'P1' },
    'lost-bid-autopsy':  { path: 'Lost Bid Autopsy → Customer Recovery', primaryWire: 'P8' },
    'scope-of-work-generator': { path: 'SOW → Bid Drafter → Margin Sentinel', primaryWire: 'W6' },
    'federal-rfp-filter': { path: 'Federal RFP → Bid Drafter → Margin Sentinel', primaryWire: 'W12' },
    'bill-rate-calculator': { path: 'Bill Rate → Bid Drafter → Margin Sentinel', primaryWire: 'W8' }
  };

  function injectWorkflowRailHeader(rail, slug) {
    var cfg = WORKFLOW_BID_CHAIN[slug];
    if (!cfg || !rail || rail.querySelector('.ot-wire-rail__header')) return;
    var header = document.createElement('div');
    header.className = 'ot-wire-rail__header';
    header.innerHTML =
      '<p class="ot-wire-rail__label">Next in your bid workflow</p>' +
      '<p class="ot-wire-rail__path">' + cfg.path + '</p>';
    rail.insertBefore(header, rail.firstChild);
    rail.classList.add('ot-wire-rail--workflow');
  }

  function _readDataAttribute(el, attr) {
    var raw = el.getAttribute('data-' + attr);
    if (!raw) return {};
    try { return JSON.parse(raw); } catch (e) { return {}; }
  }

  /* P8: Lost Bid Autopsy → Customer Recovery */
  function wireP8_LostBidToRecovery(resultContainer, data) {
    if (!resultContainer) return;
    if (resultContainer.querySelector('[data-wire="P8"]')) return;
    var payload = {
      prefill_text: (data && (data.analysis || data.summary)) || '',
      account_name: (data && data.client_name) || '',
      scope: (data && data.analysis) || ''
    };
    resultContainer.appendChild(_makeHandoffButton(
      'Open Customer Recovery →',
      'P8', 'customer-recovery', payload, 'lost-bid-autopsy', { primary: true }
    ));
  }

  function wireFingerprint(slug, container, data) {
    if (data && data._oporaWireKey) return slug + ':' + data._oporaWireKey;
    var md = data && (data.proposal_markdown || data.scope_summary || data.analysis ||
      data.recovery_plan || data.handbook_markdown || '');
    if (md) return slug + ':md:' + md.length + ':' + String(md).slice(0, 120);
    var t = (container.innerText || container.textContent || '').trim();
    return slug + ':txt:' + t.length + ':' + t.slice(0, 80);
  }

  /** Prefer the result panel (Liquid Dual-CTA lives here), never #opDocBody alone. */
  function resolveResultPanel(container) {
    if (!container) return null;
    if (container.id === 'opResult') return container;
    if (container.classList && container.classList.contains('d4-result-panel')) return container;
    var closest = container.closest && container.closest('#opResult, .d4-result-panel');
    if (closest) return closest;
    return document.getElementById('opResult') || container;
  }

  function stripAskFromProposalBody(panel) {
    stripAskHandoffChrome(panel);
    var docBody = (panel && panel.querySelector && panel.querySelector('#opDocBody')) ||
      document.getElementById('opDocBody');
    if (docBody) stripAskHandoffChrome(docBody);
  }

  function mountSecondaryAskLink() {
    /* Ask-about-this secondary CTA removed — strip orphans only */
    return;
  }

  /**
   * wirePostRenderForTool — invoke after any tool renders output.
   * Adds cross-tool CTAs + primary handoff; Ask secondary CTA is stripped.
   */
  function wirePostRenderForTool(slug, container, data) {
    if (!container || !slug) return;

    var panel = resolveResultPanel(container);
    var fp = wireFingerprint(slug, panel || container, data);
    var fpHost = panel || container;
    if (fpHost.getAttribute('data-opora-wire-key') === fp) return;
    fpHost.setAttribute('data-opora-wire-key', fp);

    data = data || {};
    stripAskHandoffChrome(fpHost);
    stripAskFromProposalBody(fpHost);
    ensurePrimaryHandoff(fpHost, slug);

    /* Bid Drafter: Liquid owns the single Margin Sentinel CTA.
       Do not inject duplicate workflow rail / W9 / yellow trust banner. */
    /* Margin Sentinel: Liquid owns Bid Drafter primary + Recovery secondary.
       Do not inject duplicate workflow rail / P1 button stack. */
    /* Review Response / Hire Right / Lost Bid: Liquid owns Dual-CTA (1 primary + 1 secondary).
       Do not inject orphan wire pills that create a third competing action. */
    if (slug === 'margin-sentinel' || slug === 'bid-drafter' || slug === 'customer-recovery' ||
        slug === 'review-response' || slug === 'hire-right' || slug === 'lost-bid-autopsy') {
      var hostSpecial = panel || container;
      var existingRailSpecial = hostSpecial.querySelector('.ot-wire-rail');
      if (existingRailSpecial && existingRailSpecial.parentNode) {
        existingRailSpecial.parentNode.removeChild(existingRailSpecial);
      }
      var trustNotesSpecial = hostSpecial.querySelectorAll('.opora-trust-note');
      for (var tns = 0; tns < trustNotesSpecial.length; tns++) {
        if (trustNotesSpecial[tns].parentNode) trustNotesSpecial[tns].parentNode.removeChild(trustNotesSpecial[tns]);
      }
      /* Strip any leftover optional-recovery pills from prior wireP1 runs */
      if (slug === 'review-response') {
        var orphanPills = hostSpecial.querySelectorAll('[data-wire="P1"], .ot-wire-btn');
        for (var op = 0; op < orphanPills.length; op++) {
          if (orphanPills[op].closest && orphanPills[op].closest('.calc-d4-primary-handoff')) continue;
          if (orphanPills[op].parentNode) orphanPills[op].parentNode.removeChild(orphanPills[op]);
        }
      }
      if (slug === 'customer-recovery') bindCrHandoffs(hostSpecial);
      if (slug === 'hire-right') bindHireRightHandoffs(hostSpecial, data);
      wireHandoffButtons();
      if (global.OporaSpine && typeof global.OporaSpine.onToolOutput === 'function') {
        global.OporaSpine.onToolOutput(slug, { container: hostSpecial, data: data });
      }
      hostSpecial.setAttribute('data-opora-wired', slug);
      return;
    }

    var rail = container.querySelector('.ot-wire-rail');
    if (!rail) {
      rail = document.createElement('div');
      rail.className = 'ot-wire-rail ot-handoffs';
      container.appendChild(rail);
    }
    injectWorkflowRailHeader(rail, slug);

    var actions = rail.querySelector('.ot-wire-rail__actions');
    if (!actions) {
      actions = document.createElement('div');
      actions.className = 'ot-wire-rail__actions';
      rail.appendChild(actions);
    } else {
      actions.innerHTML = '';
    }

    var wireTarget = actions;

    switch (slug) {
      case 'rfp-decoder':
        wireW3_RFPToBidDrafter(wireTarget, data.bid_drafter_prefill || data);
        wireW2_RFPToWalkthrough(wireTarget, data);
        wireW1_RFPToCompliance(wireTarget, data);
        break;
      case 'walkthrough-voice':
        wireW5_WalkthroughToBidDrafter(wireTarget, data);
        wireW4_WalkthroughToSOW(wireTarget, data);
        break;
      case 'compliance-agent':
        wireW7_ComplianceToBidDrafter(wireTarget, data);
        break;
      case 'bill-rate-calculator':
        wireW8_BillRateToBidDrafter(wireTarget, data);
        break;
      case 'federal-rfp-filter':
        wireW12_RFPFilterToBidDrafter(wireTarget, data);
        break;
      case 'scope-of-work-generator':
        wireW6_SOWToBidDrafter(wireTarget, data);
        wireW9_BidToMarginSentinel(wireTarget, data);
        break;
      default:
        break;
    }
    wireHandoffButtons();
    stripAskHandoffChrome(fpHost);

    if (global.OporaSpine && typeof global.OporaSpine.onToolOutput === 'function') {
      global.OporaSpine.onToolOutput(slug, { container: container, data: data });
    }
    if (global.OporaTrust && typeof global.OporaTrust.annotateContainer === 'function') {
      global.OporaTrust.annotateContainer(container);
    }
    container.setAttribute('data-opora-wired', slug);
  }

  /* ─────────────────────────────────────────────────────────────
     X2: Goal Tracking → Weekly Pulse
     Patch goal_progress into weekly pulse markdown.
  ───────────────────────────────────────────────────────────── */
  async function fetchGoalProgressForPulse(userId) {
    if (!_isSignedIn()) return null;
    try {
      var headers = await _authHeaders();
      var resp = await fetch('/api/goals?include_progress=1', { headers: headers });
      if (!resp.ok) return null;
      var data = await resp.json();
      if (!data.ok) return null;
      return data.data;
    } catch (e) { return null; }
  }

  /* ─────────────────────────────────────────────────────────────
     Init
  ───────────────────────────────────────────────────────────── */
  function _init() {
    wireHandoffButtons();
    checkHandoffContext();
    _checkPendingSave();
    _checkPendingTemplate();
    stripAskHandoffChrome(document);
    document.addEventListener('opora:tool-rendered', function (ev) {
      var d = ev && ev.detail;
      if (!d || !d.slug || !d.container) return;
      try {
        wirePostRenderForTool(d.slug, d.container, d.data || null);
      } catch (eWire) { /* never block tool completion */ }
    });
    try {
      var raw = localStorage.getItem(CALC_STORAGE_KEY);
      if (raw) global.OporaCalcOutputs = JSON.parse(raw);
    } catch (e) {}
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute('data-handoff-target') && !node._opora_wired) {
            node._opora_wired = true;
            node.addEventListener('click', function () {
              var payloadRaw = node.getAttribute('data-payload');
              var payload = null;
              if (payloadRaw) { try { payload = JSON.parse(payloadRaw); } catch (e) {} }
              handoffTo(node.getAttribute('data-handoff-target'), payload, {
                source_tool: node.getAttribute('data-source-slug') || _currentToolSlug(),
                wire_id: node.getAttribute('data-wire-id') || null,
              });
            });
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('[data-handoff-target]').forEach(function (btn) {
              if (btn._opora_wired) return;
              btn._opora_wired = true;
              btn.addEventListener('click', function () {
                var payloadRaw = btn.getAttribute('data-payload');
                var payload = null;
                if (payloadRaw) { try { payload = JSON.parse(payloadRaw); } catch (e) {} }
                handoffTo(btn.getAttribute('data-handoff-target'), payload, {
                  source_tool: btn.getAttribute('data-source-slug') || _currentToolSlug(),
                  wire_id: btn.getAttribute('data-wire-id') || null,
                });
              });
            });
            stripAskHandoffChrome(node);
          } else if (
            node.classList &&
            (node.classList.contains('ot-ask-handoff-btn') ||
              node.classList.contains('calc-d4-secondary-ask') ||
              (node.hasAttribute && node.hasAttribute('data-ask-handoff-target')))
          ) {
            if (node.closest && node.closest('.opora-edge-wc-band--ask-handoff')) return;
            if (node.parentNode) node.parentNode.removeChild(node);
          }
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }

  /* ─────────────────────────────────────────────────────────────
     F14: attachUseLastValues(opts)
     opts: { toolSlug, formId, fieldMap }  fieldMap = { apiKey: domInputId }
     - On page load (signed-in only): GET /api/tool-inputs?tool=<slug>
       If saved record found → show pill above form with timeago.
     - Pill click → fill form fields from saved record, show toast.
     - On form submit success: POST /api/tool-inputs {tool, inputs}.
       Call OporaWire.attachUseLastValues.markSaved(formId) after your
       fetch resolves to trigger the save.
  ───────────────────────────────────────────────────────────── */
  function _timeAgo(iso) {
    try {
      var diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
      if (diff < 60)   return 'just now';
      if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
      if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago';
      var d = Math.floor(diff / 86400);
      return d === 1 ? '1 day ago' : d + ' days ago';
    } catch(e) { return 'recently'; }
  }

  function attachUseLastValues(opts) {
    var toolSlug = opts.toolSlug;
    var formId   = opts.formId;
    var fieldMap = opts.fieldMap || {};
    var WORKER   = 'https://opora-ai.mshabliy.workers.dev';

    if (!toolSlug || !formId) {
      console.warn('[OporaWire] attachUseLastValues: toolSlug and formId required.');
      return;
    }

    /* Only operate for signed-in users */
    if (!_isSignedIn()) return;

    var form = document.getElementById(formId);
    if (!form) return;

    /* ── Inject pill HTML above form ──────────────────────────── */
    var pill = document.createElement('div');
    pill.className = 'ot-uselast-pill';
    pill.setAttribute('hidden', '');
    pill.innerHTML =
      '<span class="ot-uselast-arrow" aria-hidden="true">\u2190</span>' +
      '<button type="button" class="ot-uselast-btn">Use last values ' +
        '<span class="ot-uselast-time"></span></button>' +
      '<button type="button" class="ot-uselast-dismiss" aria-label="Dismiss">&times;</button>';

    /* Inject pill styles once */
    if (!document.getElementById('ot-uselast-styles')) {
      var style = document.createElement('style');
      style.id  = 'ot-uselast-styles';
      style.textContent =
        '.ot-uselast-pill{display:flex;align-items:center;gap:8px;padding:8px 12px;' +
        'margin-bottom:12px;border:1px solid rgba(15,118,110,0.30);border-radius:6px;' +
        'background:rgba(15,118,110,0.04);font-family:Inter,system-ui,sans-serif;font-size:13px;color:#374151;}' +
        '.ot-uselast-pill[hidden]{display:none;}' +
        '.ot-uselast-arrow{color:#0F766E;font-size:14px;flex-shrink:0;}' +
        '.ot-uselast-btn{background:none;border:none;cursor:pointer;padding:0;' +
        'font-size:13px;color:#0F766E;font-weight:500;text-decoration:underline;text-underline-offset:2px;}' +
        '.ot-uselast-btn:hover{color:#0F4C41;}' +
        '.ot-uselast-time{font-weight:400;color:#6B7280;text-decoration:none;}' +
        '.ot-uselast-dismiss{background:none;border:none;cursor:pointer;padding:0 2px;' +
        'font-size:16px;color:#9CA3AF;margin-left:auto;}' +
        '.ot-uselast-dismiss:hover{color:#374151;}';
      document.head.appendChild(style);
    }

    form.parentNode.insertBefore(pill, form);

    var _savedInputs = {};

    /* ── Fetch last saved values ──────────────────────────────── */
    _authHeaders().then(function(headers) {
      headers['Content-Type'] = 'application/json';
      return fetch(WORKER + '/api/tool-inputs?tool=' + encodeURIComponent(toolSlug), {
        method: 'GET', headers: headers
      });
    }).then(function(res) {
      if (!res.ok) return null;
      return res.json();
    }).then(function(data) {
      if (!data || !data.inputs) return;
      _savedInputs = data.inputs;
      var timeStr = data.updated_at ? ('from ' + _timeAgo(data.updated_at)) : '';
      pill.querySelector('.ot-uselast-time').textContent = timeStr;
      pill.removeAttribute('hidden');
    }).catch(function() {/* silent fail */});

    /* ── Fill form fields on pill click ──────────────────────── */
    pill.querySelector('.ot-uselast-btn').addEventListener('click', function() {
      var keys = Object.keys(fieldMap);
      var filled = 0;
      for (var i = 0; i < keys.length; i++) {
        var apiKey = keys[i];
        var domId  = fieldMap[apiKey];
        var val    = _savedInputs[apiKey];
        if (val === undefined || val === null) continue;
        var el = document.getElementById(domId);
        if (!el) continue;
        if (el.tagName === 'SELECT') {
          el.value = val;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (el.type === 'checkbox') {
          el.checked = !!val;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          el.value = String(val);
          el.dispatchEvent(new Event('input',  { bubbles: true }));
        }
        filled++;
      }
      if (filled > 0) { _showToast('Restored last values.', 'success'); }
      pill.setAttribute('hidden', '');
    });

    /* ── Dismiss button ───────────────────────────────────────── */
    pill.querySelector('.ot-uselast-dismiss').addEventListener('click', function() {
      pill.setAttribute('hidden', '');
    });

    /* ── Save on form submit ──────────────────────────────────── */
    form.addEventListener('ot:submit:success', function(e) {
      var inputs = {};
      var keys   = Object.keys(fieldMap);
      for (var i = 0; i < keys.length; i++) {
        var apiKey = keys[i];
        var el     = document.getElementById(fieldMap[apiKey]);
        if (!el) continue;
        inputs[apiKey] = el.type === 'checkbox' ? el.checked : el.value;
      }
      _authHeaders().then(function(headers) {
        headers['Content-Type'] = 'application/json';
        return fetch(WORKER + '/api/tool-inputs', {
          method:  'POST',
          headers: headers,
          body:    JSON.stringify({ tool: toolSlug, inputs: inputs })
        });
      }).catch(function() {/* silent fail */});
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Public API: window.OporaWire
  ───────────────────────────────────────────────────────────── */
  global.OporaWire = {
    // F1 — Save
    saveToLibrary:       saveToLibrary,
    // F2 — Share
    copyShareLink:       copyShareLink,
    // F6 — Share modal (Q30)
    showShareModal:      showShareModal,
    // F3 — Handoff
    handoffTo:           handoffTo,
    wireHandoffButtons:  wireHandoffButtons,
    checkHandoffContext: checkHandoffContext,
    // F4 — Profile
    openProfileDrawer:   openProfileDrawer,
    // F5 — Calc outputs
    writeCalcOutput:     writeCalcOutput,
    readCalcOutput:      readCalcOutput,
    resolveCalcTemplate: resolveCalcTemplate,
    // F7 — Goal (X1 dedup)
    recordGoalIncrement: recordGoalIncrement,
    // F8 — Notifications (X3)
    pushNotification:    pushNotification,
    // F9 — Error tracking (X4)
    trackToolError:      trackToolError,
    // F10 — Account tasks (P5+P6)
    checkAccountTasks:   checkAccountTasks,
    showPendingTaskPill: showPendingTaskPill,
    // F11 — Ask Opora (A4)
    wireAskHandoff:      wireAskHandoff,
    // F12 — Prompt Library (A3)
    applyPromptTemplate: applyPromptTemplate,
    // F13 — Engine (A2)
    handleEngineHandoff: handleEngineHandoff,
    // CAT2 wires
    wireW1_RFPToCompliance:      wireW1_RFPToCompliance,
    wireW2_RFPToWalkthrough:     wireW2_RFPToWalkthrough,
    wireW3_RFPToBidDrafter:      wireW3_RFPToBidDrafter,
    wireW4_WalkthroughToSOW:     wireW4_WalkthroughToSOW,
    wireW5_WalkthroughToBidDrafter: wireW5_WalkthroughToBidDrafter,
    wireW7_ComplianceToBidDrafter: wireW7_ComplianceToBidDrafter,
    wireW8_BillRateToBidDrafter: wireW8_BillRateToBidDrafter,
    wireW9_BidToMarginSentinel:  wireW9_BidToMarginSentinel,
    wireW10_BidToBidTracker:     wireW10_BidToBidTracker,
    wireW12_RFPFilterToBidDrafter: wireW12_RFPFilterToBidDrafter,
    // CAT3 wires
    wireP1_MarginToRecovery:     wireP1_MarginToRecovery,
    wireP3_ChurnToRecovery:      wireP3_ChurnToRecovery,
    wireP5P6_AccountTaskBridge:  wireP5P6_AccountTaskBridge,
    wireP7_RecoveryToBidDrafter: wireP7_RecoveryToBidDrafter,
    // CAT4 wires
    wireH1_HireRightTo1099:      wireH1_HireRightTo1099,
    wireH2_HireRightToBillRate:  wireH2_HireRightToBillRate,
    wireP8_LostBidToRecovery:    wireP8_LostBidToRecovery,
    wirePostRenderForTool:       wirePostRenderForTool,
    // CAT8 helpers
    fetchGoalProgressForPulse:   fetchGoalProgressForPulse,
    // Toast (shared)
    showToast:           _showToast,
    // F14 — Use Last Values
    attachUseLastValues: attachUseLastValues,
  };

})(window);

/* ================================================================
   BL-005 Agent C additions — auth strip, auth sheet, counter pill
   2026-06-19 — appended below wave2-addons IIFE
   ================================================================ */

/* ================================================================
   OPORA AUTH + COUNTER ADDONS — Agent C 2026-06-19
   Appended to opora-tools-wave2-addons.js IIFE via read-then-append
   pattern on the Shopify theme asset. Since theme write is blocked
   on MAIN, this runs as an IIFE appended to every page body.

   Provides:
     OporaWire.requireAuth(opts)      — check session, open sheet if needed
     OporaWire.openAuthSheet(reason)  — open slide-in panel
     OporaWire.closeAuthSheet()       — close slide-in panel
     OporaWire._sendMagicLink(email)  — POST /api/auth/magic-link
     OporaWire._onAuthed(session)     — handle auth state change
     OporaWire.isAuthed()             — returns true if session active
     OporaWire.attachCounter(opts)    — guest counter pill per tool
   ================================================================ */
(function(global) {
  'use strict';

  /* ── Session management ───────────────────────────────────────── */
  var _session = null;

  function _getSession() {
    // 1. In-memory
    if (_session) return _session;
    // 2. window.__OPORA_SESSION__
    if (global.__OPORA_SESSION__) { _session = global.__OPORA_SESSION__; return _session; }
    // 3. QA override: ?__opora_authed=true
    if (global.location && global.location.search.indexOf('__opora_authed=true') !== -1) {
      _session = { authed: true, first_name: 'Demo', email: 'demo@example.com', library_count: 3 };
      return _session;
    }
    // 4. Mock (Agent D not yet live)
    return { authed: false };
  }

  function isAuthed() {
    var s = _getSession();
    return !!(s && s.authed);
  }

  /* ── Auth sheet DOM ───────────────────────────────────────────── */
  var _sheetEl = null;
  var _sheetTrapFn = null;

  var AUTH_SHEET_HTML = [
    '<aside class="opora-auth-sheet" data-opora-auth-sheet hidden role="dialog" aria-modal="true" aria-labelledby="opora-auth-title">',
    '  <div class="opora-auth-sheet__backdrop" data-opora-auth-close></div>',
    '  <div class="opora-auth-sheet__panel" role="document">',
    '    <button type="button" class="opora-auth-sheet__close" data-opora-auth-close aria-label="Close">&#x2715;</button>',
    '    <h2 id="opora-auth-title" class="opora-auth-sheet__title">Create your free Opora account</h2>',
    '    <p class="opora-auth-sheet__sub" data-opora-auth-reason>30 seconds. No card. We will email you a one-tap link.</p>',
    '    <div class="opora-auth-sheet__google" data-google-onetap></div>',
    '    <div class="opora-auth-sheet__divider"><span>or</span></div>',
    '    <form class="opora-auth-sheet__form" data-opora-auth-form novalidate>',
    '      <label class="opora-auth-sheet__label" for="opora-auth-email">Email</label>',
    '      <input class="opora-auth-sheet__input" id="opora-auth-email" name="email" type="email" required autocomplete="email" placeholder="you@company.com">',
    '      <button class="opora-auth-sheet__submit" type="submit">Send magic link</button>',
    '    </form>',
    '    <p class="opora-auth-sheet__success" data-opora-auth-success hidden>Check your inbox &#x2014; we sent a one-tap link to <strong data-success-email></strong>.</p>',
    '    <p class="opora-auth-sheet__error" data-opora-auth-error hidden></p>',
    '    <p class="opora-auth-sheet__legal">By continuing, you agree to our <a href="/pages/terms">Terms</a> and <a href="/pages/privacy">Privacy</a>. We never sell your data.</p>',
    '  </div>',
    '</aside>'
  ].join('\n');

  var AUTH_SHEET_CSS = [
    '<style id="opora-auth-sheet-css">',
    '.opora-auth-sheet{position:fixed;inset:0;z-index:9990;display:none}',
    '.opora-auth-sheet:not([hidden]){display:block}',
    '.opora-auth-sheet__backdrop{position:absolute;inset:0;background:rgba(15,23,42,.45);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px)}',
    '.opora-auth-sheet__panel{position:absolute;right:0;top:0;bottom:0;width:420px;max-width:100vw;background:#fff;padding:32px 28px 24px;border-radius:12px 0 0 12px;box-shadow:-4px 0 32px rgba(0,0,0,.12);transform:translateX(100%);transition:transform 240ms cubic-bezier(.16,1,.3,1);overflow-y:auto}',
    '.opora-auth-sheet--open .opora-auth-sheet__panel{transform:translateX(0)}',
    '@media(max-width:480px){.opora-auth-sheet__panel{top:auto;left:0;right:0;width:100%;border-radius:16px 16px 0 0;padding:24px 20px 32px;transform:translateY(100%)}',
    '.opora-auth-sheet--open .opora-auth-sheet__panel{transform:translateY(0)}}',
    '.opora-auth-sheet__close{position:absolute;top:16px;right:16px;background:none;border:none;font-size:20px;line-height:1;color:#6b7280;cursor:pointer;padding:4px 8px;border-radius:4px}',
    '.opora-auth-sheet__close:hover{color:#1f2937;background:#f3f4f6}',
    '.opora-auth-sheet__title{font-size:20px;font-weight:700;color:#111827;margin:0 0 8px;line-height:1.3}',
    '.opora-auth-sheet__sub{font-size:14px;color:#6b7280;margin:0 0 20px;line-height:1.5}',
    '.opora-auth-sheet__google{min-height:40px;margin-bottom:12px}',
    '.opora-auth-sheet__divider{display:flex;align-items:center;gap:10px;margin:16px 0;color:#9ca3af;font-size:13px}',
    '.opora-auth-sheet__divider::before,.opora-auth-sheet__divider::after{content:"";flex:1;height:1px;background:#e5e7eb}',
    '.opora-auth-sheet__label{display:block;font-size:13px;font-weight:500;color:#374151;margin-bottom:6px}',
    '.opora-auth-sheet__input{width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:14px;color:#111827;outline:none;box-sizing:border-box;transition:border-color .15s}',
    '.opora-auth-sheet__input:focus{border-color:#0f766e;box-shadow:0 0 0 3px rgba(15,118,110,.12)}',
    '.opora-auth-sheet__submit{display:block;width:100%;margin-top:12px;padding:11px 16px;background:#0f766e;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;transition:background .15s}',
    '.opora-auth-sheet__submit:hover{background:#115e59}',
    '.opora-auth-sheet__submit:disabled{opacity:.6;cursor:not-allowed}',
    '.opora-auth-sheet__success{font-size:14px;color:#065f46;background:#ecfdf5;border:1px solid #6ee7b7;border-radius:8px;padding:10px 14px;margin-top:16px}',
    '.opora-auth-sheet__error{font-size:13px;color:#991b1b;background:#fef2f2;border:1px solid #fca5a5;border-radius:8px;padding:8px 12px;margin-top:12px}',
    '.opora-auth-sheet__legal{font-size:12px;color:#9ca3af;margin-top:20px;line-height:1.5}',
    '.opora-auth-sheet__legal a{color:#6b7280;text-decoration:underline}',
    '</style>'
  ].join('\n');

  function _injectAuthSheet() {
    if (document.getElementById('opora-auth-sheet-css')) return;
    document.head.insertAdjacentHTML('beforeend', AUTH_SHEET_CSS);
    document.body.insertAdjacentHTML('beforeend', AUTH_SHEET_HTML);
    _sheetEl = document.querySelector('[data-opora-auth-sheet]');

    // Wire close buttons
    document.querySelectorAll('[data-opora-auth-close]').forEach(function(el) {
      el.addEventListener('click', closeAuthSheet);
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && _sheetEl && !_sheetEl.hidden) closeAuthSheet();
    });

    // Wire form
    var form = document.querySelector('[data-opora-auth-form]');
    if (form) {
      form.addEventListener('submit', function(e) {
        e.preventDefault();
        var emailInput = document.getElementById('opora-auth-email');
        var email = emailInput ? emailInput.value.trim() : '';
        if (!email || !email.includes('@')) {
          var errEl = document.querySelector('[data-opora-auth-error]');
          if (errEl) { errEl.textContent = 'Enter a valid email address.'; errEl.hidden = false; }
          return;
        }
        _sendMagicLink(email);
      });
    }
  }

  function openAuthSheet(reason) {
    _injectAuthSheet();
    _sheetEl = document.querySelector('[data-opora-auth-sheet]');
    if (!_sheetEl) return;
    // Set reason text
    var reasonEl = _sheetEl.querySelector('[data-opora-auth-reason]');
    if (reasonEl && reason) reasonEl.textContent = reason;
    // Reset state
    var successEl = _sheetEl.querySelector('[data-opora-auth-success]');
    var errorEl = _sheetEl.querySelector('[data-opora-auth-error]');
    var form = _sheetEl.querySelector('[data-opora-auth-form]');
    if (successEl) successEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
    if (form) form.style.display = '';
    // Show
    _sheetEl.hidden = false;
    requestAnimationFrame(function() {
      _sheetEl.classList.add('opora-auth-sheet--open');
    });
    // Focus first input
    setTimeout(function() {
      var input = document.getElementById('opora-auth-email');
      if (input) input.focus();
    }, 250);
    // Focus trap
    _trapFocus(_sheetEl);
  }

  function closeAuthSheet() {
    if (!_sheetEl) return;
    _sheetEl.classList.remove('opora-auth-sheet--open');
    setTimeout(function() {
      if (_sheetEl) _sheetEl.hidden = true;
    }, 240);
    if (_sheetTrapFn) {
      document.removeEventListener('keydown', _sheetTrapFn);
      _sheetTrapFn = null;
    }
  }

  function _trapFocus(el) {
    var focusable = el.querySelectorAll('a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    _sheetTrapFn = function(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', _sheetTrapFn);
  }

  async function _sendMagicLink(email) {
    var submitBtn = document.querySelector('.opora-auth-sheet__submit');
    var errorEl = document.querySelector('[data-opora-auth-error]');
    var successEl = document.querySelector('[data-opora-auth-success]');
    var form = document.querySelector('[data-opora-auth-form]');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending...'; }
    try {
      var resp = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email })
      });
      var data = await resp.json();
      if (resp.ok && data.ok) {
        // Show success
        if (form) form.style.display = 'none';
        if (successEl) {
          var emailSpan = successEl.querySelector('[data-success-email]');
          if (emailSpan) emailSpan.textContent = email;
          successEl.hidden = false;
        }
        // Auto-close after 6s
        setTimeout(function() { closeAuthSheet(); }, 6000);
      } else {
        var msg = (data.error) || 'Could not send magic link. Try again.';
        if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send magic link'; }
      }
    } catch(err) {
      if (errorEl) { errorEl.textContent = 'Network error. Check your connection and try again.'; errorEl.hidden = false; }
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send magic link'; }
    }
  }

  function requireAuth(opts) {
    // opts = { reason, onAuthed, pendingAction }
    opts = opts || {};
    if (isAuthed()) {
      if (opts.onAuthed) opts.onAuthed(_getSession());
      return;
    }
    // Store pending action
    if (opts.pendingAction) {
      try { sessionStorage.setItem('opora_pending_action', JSON.stringify(opts.pendingAction)); } catch(e){}
    }
    if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function' && !global.OporaAuth.__oporaStub) {
      global.OporaAuth.openModal({
        intent: 'sign-up',
        inPlace: true,
        title: 'Save this result',
        tagline: opts.reason || 'Create a free account to edit, duplicate, and access this result across devices.',
        onAuthed: typeof opts.onAuthed === 'function' ? opts.onAuthed : null
      });
      return;
    }
    openAuthSheet(opts.reason || '30 seconds. No card. We will email you a one-tap link.');
  }

  function _onAuthed(session) {
    _session = session;
    // 1. Set body[data-authed=true]
    document.body.setAttribute('data-authed', 'true');
    global.__OPORA_SESSION__ = session;
    // 2. Update header strip
    var initialEl = document.querySelector('[data-user-initial]');
    if (initialEl && session.first_name) initialEl.textContent = (session.first_name[0] || 'A').toUpperCase();
    var libCountEl = document.querySelector('[data-library-count]');
    if (libCountEl) libCountEl.textContent = '(' + (session.library_count || 0) + ')';
    // 3. Replay pending action
    try {
      var pending = JSON.parse(sessionStorage.getItem('opora_pending_action') || 'null');
      if (pending) {
        sessionStorage.removeItem('opora_pending_action');
        document.dispatchEvent(new CustomEvent('opora:pending-action', { detail: pending }));
      }
    } catch(e){}
    // 4. Close sheet
    closeAuthSheet();
    // 5. New-user welcome redirect (Agent E handoff)
    // Send brand-new users with no library activity to /account/welcome
    try {
      var u = session && session.user;
      if (u && (session.library_count === 0 || session.library_count == null)) {
        var createdAt = u.created_at || 0;
        var ageSec = (Date.now() / 1000) - createdAt;
        // First 30 minutes after signup, and only if not already on /account/welcome
        if (createdAt && ageSec < 1800 && location.pathname.indexOf('/account/welcome') !== 0) {
          var fn = encodeURIComponent(u.first_name || 'operator');
          // Brief delay so the auth-success state can render before redirect
          setTimeout(function(){ window.location.href = '/account/welcome?first_name=' + fn; }, 350);
        }
      }
    } catch(e) { /* non-fatal */ }
  }

  /* ── Auth strip UI ────────────────────────────────────────────── */
  var AUTH_STRIP_CSS = [
    '<style id="opora-auth-strip-css">',
    '.opora-auth-strip{display:inline-flex;align-items:center;gap:12px;font-family:Inter,system-ui,sans-serif}',
    '.opora-auth-strip__guest,.opora-auth-strip__authed{display:flex;align-items:center;gap:12px}',
    '.opora-auth-strip__signin{color:#fff;font-size:14px;font-weight:500;text-decoration:none;padding:8px 4px;opacity:.85}',
    '.opora-auth-strip__signin:hover{opacity:1}',
    '.opora-auth-strip__cta{display:inline-flex;align-items:center;padding:8px 16px;background:#0f766e;color:#fff;font-size:14px;font-weight:600;border-radius:6px;text-decoration:none;transition:background .15s}',
    '.opora-auth-strip__cta:hover{background:#115e59}',
    '.opora-auth-strip__library{color:#fff;font-size:14px;font-weight:500;text-decoration:none;opacity:.9}',
    '.opora-auth-strip__library:hover{opacity:1}',
    '.opora-auth-strip__library [data-library-count]{color:rgba(255,255,255,.65);font-weight:400}',
    '.opora-auth-strip__avatar{width:32px;height:32px;border-radius:50%;background:#0f766e;color:#fff;font-size:13px;font-weight:600;display:inline-flex;align-items:center;justify-content:center;border:2px solid rgba(255,255,255,.3);cursor:pointer}',
    '.opora-auth-strip__avatar:hover{background:#115e59}',
    'body[data-authed="true"] .opora-auth-strip__guest{display:none!important}',
    'body[data-authed="true"] .opora-auth-strip__authed{display:flex!important}',
    'body[data-authed="true"] .site-header,body[data-authed="true"] header.header{border-top:2px solid #0f766e}',
    '</style>'
  ].join('\n');

  var AUTH_STRIP_HTML = [
    '<div class="opora-auth-strip" data-opora-auth-strip id="opora-auth-strip-widget">',
    '  <div class="opora-auth-strip__guest">',
    '    <a href="#" class="opora-auth-strip__signin" data-opora-auth-trigger="signin">Sign in</a>',
    '    <a href="#" class="opora-auth-strip__cta" data-opora-auth-trigger="register">Get started</a>',
    '  </div>',
    '  <div class="opora-auth-strip__authed" style="display:none">',
    '    <a href="/account/library" class="opora-auth-strip__library">My Library <span data-library-count>(0)</span></a>',
    '    <button type="button" class="opora-auth-strip__avatar" data-opora-account-menu aria-label="Account menu">',
    '      <span data-user-initial>A</span>',
    '    </button>',
    '  </div>',
    '</div>'
  ].join('\n');

  function _injectAuthStrip() {
    if (document.getElementById('opora-auth-strip-widget')) return;
    // Try to find header actions region
    var target = document.querySelector('.header__actions') ||
                 document.querySelector('.header__right') ||
                 document.querySelector('[data-header-actions]') ||
                 document.querySelector('.header__inner');
    if (!target) return;
    if (!document.getElementById('opora-auth-strip-css')) {
      document.head.insertAdjacentHTML('beforeend', AUTH_STRIP_CSS);
    }
    target.insertAdjacentHTML('beforeend', AUTH_STRIP_HTML);

    // Wire trigger buttons
    document.querySelectorAll('[data-opora-auth-trigger]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        e.preventDefault();
        var reason = el.dataset.oporaAuthTrigger === 'signin'
          ? '30 seconds. No card. We will email you a one-tap link.'
          : 'Create your free account to get started.';
        openAuthSheet(reason);
      });
    });

    // Wire account menu button
    var avatarBtn = document.querySelector('[data-opora-account-menu]');
    if (avatarBtn) {
      avatarBtn.addEventListener('click', function() {
        if (typeof OporaWire !== 'undefined' && typeof OporaWire.openProfileDrawer === 'function') {
          OporaWire.openProfileDrawer();
        }
      });
    }

    // Apply authed state if already authed
    if (isAuthed()) {
      var s = _getSession();
      _onAuthed(s);
    }
  }

  /* ── Session check on page load ─────────────────────────────────── */
  function _checkSessionOnLoad() {
    // /api/auth/session is NOT served on the storefront origin (returns 404),
    // which produced a failed request in the console on every tool page (§7.7).
    // Auth state is sourced from Clerk + the local session helper instead, so we
    // skip the dead poll. Flip back to true only if a real endpoint is deployed.
    var agentDLive = false;
    if (agentDLive) {
      fetch('/api/auth/session', { credentials: 'include' })
        .then(function(r) { return r.json(); })
        .then(function(s) {
          if (s && s.authed) _onAuthed(s);
        })
        .catch(function() {});
    } else {
      // Use mock / QA override
      var s = _getSession();
      if (s && s.authed) _onAuthed(s);
    }
  }

  /* ── Counter pill ─────────────────────────────────────────────── */
  var COUNTER_CSS = [
    '<style id="opora-counter-pill-css">',
    '.opora-counter-pill{display:none;align-items:center;gap:10px;flex-wrap:wrap;padding:9px 14px;border:1px solid #e5e7eb;background:#f9fafb;border-radius:8px;font-size:13px;color:#374151;margin-bottom:16px}',
    '.opora-counter-pill--visible{display:flex}',
    '.opora-counter-pill--amber{border-color:#f59e0b;background:#fffbeb;color:#92400e}',
    '.opora-counter-pill--full{border-color:#fca5a5;background:#fef2f2;color:#991b1b}',
    '.opora-counter-pill__cta{margin-left:auto;color:#0f766e;font-weight:600;text-decoration:none;white-space:nowrap;font-size:12px}',
    '.opora-counter-pill__cta:hover{text-decoration:underline}',
    '</style>'
  ].join('\n');

  function attachCounter(opts) {
    // opts = { toolSlug, displayName, cap }
    if (!opts || !opts.toolSlug) return;

    // If authed, never show pill
    if (isAuthed()) return;

    var toolSlug = opts.toolSlug;
    var displayName = opts.displayName || toolSlug;
    var cap = opts.cap || 2;

    // Inject CSS once
    if (!document.getElementById('opora-counter-pill-css')) {
      document.head.insertAdjacentHTML('beforeend', COUNTER_CSS);
    }

    // Create pill element
    var pill = document.createElement('div');
    pill.className = 'opora-counter-pill';
    pill.setAttribute('data-opora-counter', '');
    pill.innerHTML = '<span data-opora-counter-text>0 of ' + cap + ' free ' + displayName + 's this month</span>' +
      '<a href="#" class="opora-counter-pill__cta" data-opora-auth-trigger="register">Create a free account for unlimited &#x2192;</a>';

    // Wire CTA
    var cta = pill.querySelector('[data-opora-auth-trigger]');
    if (cta) {
      cta.addEventListener('click', function(e) {
        e.preventDefault();
        requireAuth({ reason: 'Create a free account for unlimited access.', pendingAction: { type: 'counter_cta', tool: toolSlug } });
      });
    }

    // Find result panel to insert before
    function _insertPill() {
      var resultPanel = document.querySelector('.ot-result-body') ||
                        document.querySelector('[id$="-output"]') ||
                        document.querySelector('[id*="result"]');
      if (resultPanel && resultPanel.parentNode && !document.querySelector('[data-opora-counter]')) {
        resultPanel.parentNode.insertBefore(pill, resultPanel);
      } else if (!document.querySelector('[data-opora-counter]')) {
        document.body.appendChild(pill);
      }
    }
    _insertPill();

    // Listen for successful tool completion
    document.addEventListener('opora:tool-complete', function(e) {
      if (isAuthed()) return;
      // Fetch usage count
      fetch('/api/limits/check?tool=' + encodeURIComponent(toolSlug), { credentials: 'include' })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var used = data.used || 0;
          var serverCap = data.cap || cap;
          var pct = serverCap > 0 ? (used / serverCap) : 0;
          var textEl = pill.querySelector('[data-opora-counter-text]');
          if (textEl) textEl.textContent = used + ' of ' + serverCap + ' free ' + displayName + 's this month';
          pill.classList.add('opora-counter-pill--visible');
          pill.classList.remove('opora-counter-pill--amber', 'opora-counter-pill--full');
          if (pct >= 1) {
            pill.classList.add('opora-counter-pill--full');
            // Auto-open auth sheet after result renders
            setTimeout(function() {
              requireAuth({ reason: 'You have used all your free ' + displayName + 's this month. Create a free account for unlimited access.' });
            }, 800);
          } else if (pct >= 0.8) {
            pill.classList.add('opora-counter-pill--amber');
          }
        })
        .catch(function() {
          // Fallback: show pill with local count estimate (no network)
          pill.classList.add('opora-counter-pill--visible');
        });
    });
  }

  /* ── Hero personalization (C6) ────────────────────────────────── */
  function _heroPersonalize(session) {
    var onHome = (global.location.pathname === '/' || global.location.pathname === '/pages/home');
    if (!onHome) return;
    var h1 = document.querySelector('.oa-hero__h1, .hero__title h1, .hero h1, [data-hero-h1]');
    if (!h1) return;
    var first = (session.first_name || '').trim();
    if (!first) return;
    h1.textContent = 'Welcome back, ' + first + '.';
    // Add "pick up where you left off" link below if not already there
    var existing = document.getElementById('opora-hero-cta');
    if (!existing) {
      var link = document.createElement('a');
      link.id = 'opora-hero-cta';
      link.href = '/account/library';
      link.textContent = 'Pick up where you left off';
      link.style.cssText = 'display:inline-block;margin-top:12px;font-size:15px;font-weight:500;color:#0f766e;text-decoration:none';
      link.setAttribute('aria-label', 'Go to your library');
      var arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = ' \u2192';
      link.appendChild(arrow);
      h1.parentNode && h1.parentNode.insertBefore(link, h1.nextSibling);
    }
  }

  /* ── Init on DOMContentLoaded ─────────────────────────────────── */
  function _init() {
    _injectAuthStrip();
    _checkSessionOnLoad();
    // Expose updated _onAuthed (extends from above)
    var _origOnAuthed = global.OporaWire && global.OporaWire._onAuthed;
    function _enhancedOnAuthed(session) {
      if (_origOnAuthed) _origOnAuthed(session);
      _onAuthed(session);
      _heroPersonalize(session);
    }
    if (global.OporaWire) {
      global.OporaWire.requireAuth    = requireAuth;
      global.OporaWire.openAuthSheet  = openAuthSheet;
      global.OporaWire.closeAuthSheet = closeAuthSheet;
      global.OporaWire._sendMagicLink = _sendMagicLink;
      global.OporaWire._onAuthed      = _enhancedOnAuthed;
      global.OporaWire.isAuthed       = isAuthed;
      global.OporaWire.attachCounter  = attachCounter;
    } else {
      // OporaWire not yet loaded — wait for it
      var _waitAttempts = 0;
      var _waitInterval = setInterval(function() {
        _waitAttempts++;
        if (global.OporaWire) {
          clearInterval(_waitInterval);
          global.OporaWire.requireAuth    = requireAuth;
          global.OporaWire.openAuthSheet  = openAuthSheet;
          global.OporaWire.closeAuthSheet = closeAuthSheet;
          global.OporaWire._sendMagicLink = _sendMagicLink;
          global.OporaWire._onAuthed      = _enhancedOnAuthed;
          global.OporaWire.isAuthed       = isAuthed;
          global.OporaWire.attachCounter  = attachCounter;
        }
        if (_waitAttempts > 50) clearInterval(_waitInterval);
      }, 100);
    }

    // Wire Save/Share/Export buttons site-wide for auth gate
    document.querySelectorAll('[data-needs-auth]').forEach(function(el) {
      el.addEventListener('click', function(e) {
        if (!isAuthed()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          requireAuth({ reason: 'Sign in to save and export your results.', pendingAction: { type: 'button_click', label: el.textContent.trim() } });
        }
      }, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }


  /* ─────────────────────────────────────────────────────────────
     P5 — Post-result sign-in nudge (v13.16)
     After guest runs a tool, shows an inline card below #opResult
  ───────────────────────────────────────────────────────────── */
  (function() {
    var NUDGE_CSS = [
      '<style id="opora-anon-nudge-v13-css">',
      '.opora-anon-nudge-card{display:none;margin:24px 0 0;padding:16px 20px;background:#f0fdfa;border:1px solid #99f6e4;border-radius:10px;align-items:center;gap:16px;flex-wrap:wrap}',
      '.opora-anon-nudge-card--visible{display:flex}',
      '.opora-anon-nudge-card__text{flex:1;min-width:200px;font-size:14px;color:#134e4a;line-height:1.5}',
      '.opora-anon-nudge-card__btn{flex-shrink:0;padding:9px 18px;background:#0f766e;color:#fff;border:none;border-radius:7px;font-size:14px;font-weight:600;cursor:pointer;transition:background .15s;text-decoration:none;display:inline-block}',
      '.opora-anon-nudge-card__btn:hover{background:#115e59}',
      '</style>'
    ].join('\n');

    function _injectNudge() {
      if (isAuthed()) return;
      var result = document.getElementById('opResult');
      if (!result) return;
      var existing = document.getElementById('opora-anon-nudge-v13');
      if (existing) existing.remove();
      if (!document.getElementById('opora-anon-nudge-v13-css')) {
        document.head.insertAdjacentHTML('beforeend', NUDGE_CSS);
      }
      var nudge = document.createElement('div');
      nudge.id = 'opora-anon-nudge-v13';
      nudge.className = 'opora-anon-nudge-card opora-anon-nudge-card--visible';
      nudge.setAttribute('role', 'complementary');
      nudge.setAttribute('aria-label', 'Save and share your result');
      nudge.innerHTML =
        '<p class="opora-anon-nudge-card__text">Sign in to save, share, and export this — and unlock 25 runs per month.</p>' +
        '<button type="button" class="opora-anon-nudge-card__btn" data-opora-nudge-signin>Sign in</button>';
      result.insertAdjacentElement('afterend', nudge);
      var btn = nudge.querySelector('[data-opora-nudge-signin]');
      if (btn) {
        btn.addEventListener('click', function() {
          if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
            global.OporaAuth.openModal({
              intent: 'sign-up',
              inPlace: true,
              title: 'Save this result',
              tagline: 'Create a free account to edit, duplicate, and access this result across devices.'
            });
          } else {
            requireAuth({ reason: 'Sign in to save, share, and export your results.' });
          }
        });
      }
    }

    document.addEventListener('opora:tool-complete', function() {
      if (isAuthed()) return;
      setTimeout(_injectNudge, 400);
    });
  }());

  /* ─────────────────────────────────────────────────────────────
     P6 — Lock icons on Save/Share/Export buttons for guests (v13.16)
  ───────────────────────────────────────────────────────────── */
  (function() {
    var LOCK_SVG = '<svg class="opora-lock-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="M5 7V5a3 3 0 0 1 6 0v2M3 7h10v7H3z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    var LOCK_SELECTORS = [
      '.opora-save-btn',
      '.opora-share-btn',
      '.opora-export-btn',
      '#ot-json-btn-bill-rate'
    ];

    function _lockButtons() {
      if (isAuthed()) return;
      LOCK_SELECTORS.forEach(function(sel) {
        document.querySelectorAll(sel).forEach(function(btn) {
          if (btn.dataset.oporaLocked) return;
          btn.dataset.oporaLocked = '1';
          btn.insertAdjacentHTML('afterbegin', LOCK_SVG);
          btn.addEventListener('click', function(e) {
            if (isAuthed()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (global.OporaAuth && typeof global.OporaAuth.openModal === 'function') {
              global.OporaAuth.openModal({
                intent: 'sign-up',
                inPlace: true,
                title: 'Save this result',
                tagline: 'Create a free account to edit, duplicate, and access this result across devices.'
              });
            } else {
              requireAuth({ reason: 'Sign in to save, share, and export your results.' });
            }
          }, true);
        });
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', _lockButtons);
    } else {
      _lockButtons();
    }
    document.addEventListener('opora:tool-complete', function() {
      setTimeout(_lockButtons, 300);
    });
  }());

})(window);


/* ================================================================
   BL-005 Agent C — C2: Promo strip override + C7: Resources label
   2026-06-19 — appended to opora-tools-wave2-addons.js
   ================================================================ */
(function() {
  'use strict';

  /* ── C2: Rewrite promo strip text ────────────────────────────── */
  function _fixPromoStrip() {
    // Target the announcement bar text element
    var barEl = document.querySelector('.announcement-bar__content');
    if (!barEl) return;
    var text = (barEl.textContent || '').trim();
    // Replace any variant of the old promo text
    if (text && (
      text.indexOf('3 free run') !== -1 ||
      text.indexOf('30-day trial') !== -1 ||
      text.indexOf('Opora Edge') !== -1 ||
      text.indexOf('NEW') !== -1 ||
      text.indexOf('free runs') !== -1
    )) {
      barEl.textContent = 'Free to use today \u2014 no subscription';
      // Remove any "NEW" badge sibling elements
      var parent = barEl.parentNode;
      if (parent) {
        var badges = parent.querySelectorAll('.badge, .badge--new, [class*="new-badge"], [class*="badge"]');
        badges.forEach(function(b) { b.remove(); });
      }
    }
  }

  /* ── C7: Resources label — never inject into page breadcrumbs ──
     Bug (live): header "Operator Blueprint" is a <button>, not <a>, so the old
     `nav a` matcher grabbed the breadcrumb link and injected an all-caps
     "RESOURCES" span into .opora-bc (missing ›, broken alignment/weight).
     Fix: strip any crumb pollution. Do not re-inject a header group label here —
     top-nav IA already lists Operator Blueprint / Field Guide as peers. */
  function _fixResourcesLabel() {
    document.querySelectorAll(
      '.opora-bc .opora-resources-nav-label, .fg-hero__breadcrumb .opora-resources-nav-label, .opora-prog__crumbs .opora-resources-nav-label, .opora-hub-breadcrumb .opora-resources-nav-label, nav[aria-label="Breadcrumb"] .opora-resources-nav-label'
    ).forEach(function(el) { el.remove(); });
  }

  function _runCFixes() {
    _fixPromoStrip();
    _fixResourcesLabel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _runCFixes);
  } else {
    _runCFixes();
  }

  // Also run after a short delay for dynamic content
  setTimeout(_runCFixes, 500);
})();

/* ============================================================
   BL-005 AGENT E — Onboarding Layer 3 & 4
   E3: OporaWire.coachMark — ambient pulse dot + popover
   E4: OporaWire.initProgressChip — header progress chip + drawer
   E5: OporaWire.initHeroChip — homepage first-visit chip
   ============================================================ */

(function(global) {
  'use strict';

  var WORKER = 'https://opora-ai.mshabliy.workers.dev';

  /* ─────────────────────────────────────────────────────────────
     Internal: session check (reuse existing or check cookie)
  ───────────────────────────────────────────────────────────── */
  function _isAuthed() {
    // Prefer window.__OPORA_SESSION__ set by auth strip
    if (global.__OPORA_SESSION__ && global.__OPORA_SESSION__.authed) return true;
    // Check OporaWire / OporaAccount
    if (global.OporaWire && typeof global.OporaWire.isAuthed === 'function') return global.OporaWire.isAuthed();
    if (global.OporaAccount && typeof global.OporaAccount.isSignedIn === 'function') return global.OporaAccount.isSignedIn();
    // Check session cookie presence
    return document.cookie.indexOf('__Host-opora-session=') !== -1;
  }

  /* ─────────────────────────────────────────────────────────────
     Internal: inject coach mark CSS once
  ───────────────────────────────────────────────────────────── */
  var _coachCssInjected = false;
  function _injectCoachCSS() {
    if (_coachCssInjected) return;
    _coachCssInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.opora-coach-dot{position:absolute;width:6px;height:6px;border-radius:50%;background:#0f766e;cursor:pointer;z-index:90}',
      '.opora-coach-dot::before{content:"";position:absolute;inset:-6px;border-radius:50%;border:2px solid rgba(15,118,110,0.4);animation:opora-coach-pulse 2s ease-out infinite}',
      '@keyframes opora-coach-pulse{0%{transform:scale(0.7);opacity:1}100%{transform:scale(2);opacity:0}}',
      '.opora-coach-popover{position:absolute;z-index:200;width:240px;background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:12px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.12);font-family:Inter,system-ui,sans-serif}',
      '.opora-coach-popover__copy{font-size:13px;line-height:1.5;color:#374151;margin:0 0 10px}',
      '.opora-coach-popover__dismiss{font-size:12px;font-weight:500;color:#0f766e;background:none;border:none;padding:0;cursor:pointer;text-decoration:underline}',
      '.opora-progress-chip{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border:1px solid #e5e7eb;background:#fff;border-radius:20px;font-size:12px;font-weight:500;color:#374151;cursor:pointer;font-family:Inter,system-ui,sans-serif;transition:border-color .15s}',
      '.opora-progress-chip:hover{border-color:#0f766e;color:#0f766e}',
      '.opora-progress-drawer{position:absolute;top:100%;right:0;margin-top:4px;width:320px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,0.12);padding:16px;z-index:300;font-family:Inter,system-ui,sans-serif}',
      '.opora-progress-drawer h4{font-size:14px;font-weight:600;color:#111827;margin:0 0 12px}',
      '.opora-progress-step{display:flex;align-items:center;gap:8px;font-size:13px;color:#374151;margin:0 0 8px}',
      '.opora-progress-step--done{color:#6b7280;text-decoration:none}',
      '.opora-progress-step__check{width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0}',
      '.opora-progress-step__check--done{background:#0f766e}',
      '.opora-progress-step__check--pending{border:1.5px solid #d1d5db}',
      '.opora-progress-hide{display:block;margin-top:12px;font-size:12px;color:#9ca3af;background:none;border:none;padding:0;cursor:pointer;text-decoration:none}',
      '.opora-progress-hide:hover{color:#0f766e}',
      '.opora-hero-chip{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;background:#fff;border:1px solid #e5e7eb;border-radius:20px;font-size:13px;font-weight:500;color:#374151;text-decoration:none;font-family:Inter,system-ui,sans-serif;transition:border-color .15s,color .15s}',
      '.opora-hero-chip:hover{border-color:#0f766e;color:#0f766e}'
    ].join('');
    document.head.appendChild(style);
  }

  /* ─────────────────────────────────────────────────────────────
     E3: coachMark(markId, anchorSelector, copy)
  ───────────────────────────────────────────────────────────── */
  var _coachMarksSeen = {};
  var _coachMarksLoaded = false;

  function _loadCoachMarksSeen(cb) {
    if (_coachMarksLoaded) { cb(); return; }
    if (!_isAuthed()) { _coachMarksLoaded = true; cb(); return; }
    fetch(WORKER + '/api/coach-marks', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : { seen: [] }; })
      .then(function(d) {
        var seen = d.seen || [];
        for (var i = 0; i < seen.length; i++) _coachMarksSeen[seen[i]] = true;
        _coachMarksLoaded = true;
        cb();
      })
      .catch(function() { _coachMarksLoaded = true; cb(); });
  }

  function coachMark(markId, anchorSelector, copy) {
    // 1. Not authed — noop
    if (!_isAuthed()) return;

    _loadCoachMarksSeen(function() {
      // 2. Already seen — noop
      if (_coachMarksSeen[markId]) return;

      // 3. Find anchor
      var anchor = document.querySelector(anchorSelector);
      if (!anchor) return;

      _injectCoachCSS();

      // 4. Render pulse dot at top-right of anchor
      var dot = document.createElement('span');
      dot.className = 'opora-coach-dot';
      dot.setAttribute('aria-label', 'New tip: ' + copy);
      dot.setAttribute('role', 'button');
      dot.setAttribute('tabindex', '0');

      // Position relative to anchor
      var anchorParent = anchor.parentElement;
      if (anchorParent && getComputedStyle(anchorParent).position === 'static') {
        anchorParent.style.position = 'relative';
      }
      anchor.insertAdjacentElement('afterend', dot);

      var popoverOpen = false;
      var popoverEl = null;

      function openPopover() {
        if (popoverOpen) return;
        popoverOpen = true;
        popoverEl = document.createElement('div');
        popoverEl.className = 'opora-coach-popover';
        popoverEl.setAttribute('role', 'tooltip');
        popoverEl.innerHTML =
          '<p class="opora-coach-popover__copy">' + copy + '</p>' +
          '<button class="opora-coach-popover__dismiss" type="button">Got it</button>';
        popoverEl.querySelector('.opora-coach-popover__dismiss').addEventListener('click', dismiss);
        dot.insertAdjacentElement('afterend', popoverEl);
      }

      function dismiss() {
        if (popoverEl) { popoverEl.remove(); popoverEl = null; }
        dot.remove();
        _coachMarksSeen[markId] = true;
        // 6. POST to backend
        fetch(WORKER + '/api/coach-marks/seen', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mark_id: markId })
        }).catch(function() {});
      }

      // 5. Click dot → open popover
      dot.addEventListener('click', openPopover);
      dot.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPopover(); }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     E3: Bootstrap 4 coach mark triggers
  ───────────────────────────────────────────────────────────── */
  function _bootstrapCoachMarks() {
    if (!_isAuthed()) return;

    // save-to-library: after opora:tool-complete fires
    document.addEventListener('opora:tool-complete', function() {
      setTimeout(function() {
        coachMark('save-to-library', '#ot-save-btn', 'Save this result. It comes back next time you open the tool, on any device.');
      }, 800);
    }, { once: true });

    // cross-tool-handoff: when handoff strip renders (not hidden)
    var handoffStrips = document.querySelectorAll('[id^="ot-handoff-strip"]');
    handoffStrips.forEach(function(strip) {
      var obs = new MutationObserver(function() {
        if (!strip.hidden) {
          var firstBtn = strip.querySelector('.ot-handoff-btn');
          if (firstBtn) {
            coachMark('cross-tool-handoff', '[id^="ot-handoff-strip"] .ot-handoff-btn', 'Pass this into another tool — your work pre-fills automatically.');
          }
          obs.disconnect();
        }
      });
      obs.observe(strip, { attributes: true, attributeFilter: ['hidden'] });
    });

    // use-last-values: if user has visited this tool before (sessionStorage counter)
    var toolSlug = window.location.pathname.replace('/pages/', '');
    var visitKey = 'opora-visit-count-' + toolSlug;
    var visitCount = parseInt(sessionStorage.getItem(visitKey) || '0', 10) + 1;
    sessionStorage.setItem(visitKey, String(visitCount));
    if (visitCount >= 2) {
      setTimeout(function() {
        coachMark('use-last-values', '.ot-uselast-pill', 'Want last time\'s inputs? Use last values restores everything.');
      }, 1200);
    }

    // ask-opora: after 3 total tool runs (tracked via localStorage)
    var runKey = 'opora-total-runs';
    var runs = parseInt(localStorage.getItem(runKey) || '0', 10);
    document.addEventListener('opora:tool-complete', function() {
      var newRuns = parseInt(localStorage.getItem(runKey) || '0', 10) + 1;
      localStorage.setItem(runKey, String(newRuns));
      if (newRuns >= 3) {
        setTimeout(function() {
          coachMark('ask-opora', '#ot-ask-about-btn, [href*="ask-opora"]', 'Have a question across all your work? Ask Opora has the context.');
        }, 1000);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     E4: initProgressChip — header progress chip + drawer
  ───────────────────────────────────────────────────────────── */
  function initProgressChip() {
    if (!_isAuthed()) return;

    // Check hidden cookie
    if (document.cookie.indexOf('opora-progress-hidden=1') !== -1) return;
    if (document.cookie.indexOf('opora-progress-completed=1') !== -1) return;

    fetch(WORKER + '/api/user/progress', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) {
        if (!d || !d.ok) return;
        if (d.completed >= d.total) return; // all done, don't show

        _injectCoachCSS();
        _renderProgressChip(d);
      })
      .catch(function() {});
  }

  function _renderProgressChip(d) {
    // Find auth strip or header for injection
    var header = document.querySelector(
      '[data-opora-auth-strip], .opora-auth-strip, .header__actions, header .header__right, nav .nav__end'
    );

    var chipWrap = document.createElement('div');
    chipWrap.style.cssText = 'position:relative;display:inline-block;';

    var chip = document.createElement('button');
    chip.className = 'opora-progress-chip';
    chip.setAttribute('data-opora-progress', '');
    chip.setAttribute('aria-label', 'Getting started — ' + d.completed + ' of ' + d.total + ' steps complete');
    chip.innerHTML = 'Getting started &middot; <span data-opora-progress-count>' + d.completed + ' of ' + d.total + '</span>';

    chipWrap.appendChild(chip);
    if (header) {
      header.appendChild(chipWrap);
    } else {
      // Inject via fixed position if no header found
      chipWrap.style.cssText += 'position:fixed;top:16px;right:80px;z-index:400;';
      document.body.appendChild(chipWrap);
    }

    var drawerOpen = false;
    var drawer = null;

    chip.addEventListener('click', function() {
      if (drawerOpen && drawer) {
        drawer.remove(); drawer = null; drawerOpen = false;
        return;
      }
      drawerOpen = true;
      drawer = document.createElement('div');
      drawer.className = 'opora-progress-drawer';
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-label', 'Getting started checklist');

      var stepsHtml = d.steps.map(function(s) {
        var done = s.done;
        return '<div class="opora-progress-step' + (done ? ' opora-progress-step--done' : '') + '">' +
          '<span class="opora-progress-step__check ' + (done ? 'opora-progress-step__check--done' : 'opora-progress-step__check--pending') + '">' +
          (done ? '<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '') +
          '</span>' +
          s.label +
          '</div>';
      }).join('');

      drawer.innerHTML = '<h4>Getting started</h4>' + stepsHtml +
        '<button class="opora-progress-hide" type="button">Hide this &#x2192;</button>';

      drawer.querySelector('.opora-progress-hide').addEventListener('click', function() {
        drawer.remove(); drawer = null; drawerOpen = false;
        chip.parentElement.remove();
        document.cookie = 'opora-progress-hidden=1;path=/;max-age=31536000;samesite=lax';
      });

      chipWrap.appendChild(drawer);
    });

    // Close on outside click
    document.addEventListener('click', function(e) {
      if (drawer && !chipWrap.contains(e.target)) {
        drawer.remove(); drawer = null; drawerOpen = false;
      }
    });

    // Check if complete after 5/5
    if (d.completed >= d.total) {
      chip.parentElement && chip.parentElement.remove();
      document.cookie = 'opora-progress-completed=1;path=/;max-age=31536000;samesite=lax';
      // Toast bottom-center
      if (global.OporaWire && global.OporaWire.showToast) {
        global.OporaWire.showToast("You're set. We'll stay out of the way from here.", 'success');
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────
     E5: initHeroChip — homepage first-visit chip (signed-out)
  ───────────────────────────────────────────────────────────── */
  function initHeroChip() {
    // Only on homepage
    var p = window.location.pathname;
    if (p !== '/' && p !== '/pages/home' && p !== '') return;

    // Only if not authed
    if (_isAuthed()) return;

    // Check seen cookie
    if (document.cookie.indexOf('opora-seen-hero-chip=1') !== -1) return;

    setTimeout(function() {
      _injectCoachCSS();

      var chip = document.createElement('a');
      chip.className = 'opora-hero-chip';
      chip.href = '/pages/rfp-decoder';
      chip.textContent = 'New here? Start with the RFP Decoder \u2192';

      // Find hero section
      var hero = document.querySelector(
        '.hero, .ot-hero, [class*="hero"], section:first-of-type, .homepage-section:first-child'
      );

      if (hero) {
        // Position as block below hero subtitle on mobile, absolute top-right on desktop
        chip.style.cssText = 'margin-top:16px;display:inline-flex;';
        var heroSub = hero.querySelector('p, .subtitle, .lede, [class*="lede"], [class*="desc"]');
        if (heroSub) {
          heroSub.insertAdjacentElement('afterend', chip);
        } else {
          hero.appendChild(chip);
        }
      } else {
        // Fallback: inject after first h1
        var h1 = document.querySelector('h1');
        if (h1) h1.insertAdjacentElement('afterend', chip);
        else document.body.insertBefore(chip, document.body.firstChild);
      }

      // Set cookie so it doesn't show again
      document.cookie = 'opora-seen-hero-chip=1;path=/;max-age=31536000;samesite=lax';
    }, 200);
  }

  /* ─────────────────────────────────────────────────────────────
     Extend OporaWire with new E3/E4/E5 methods
  ───────────────────────────────────────────────────────────── */
  function _extendOporaWire() {
    if (!global.OporaWire) {
      global.OporaWire = {};
    }
    global.OporaWire.coachMark         = coachMark;
    global.OporaWire.initProgressChip  = initProgressChip;
    global.OporaWire.initHeroChip      = initHeroChip;
    global.OporaWire._coachMarksSeen   = _coachMarksSeen;
  }

  /* ─────────────────────────────────────────────────────────────
     Auto-init on DOMContentLoaded
  ───────────────────────────────────────────────────────────── */
  function _autoInit() {
    _extendOporaWire();
    _bootstrapCoachMarks();
    initProgressChip();
    initHeroChip();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _autoInit);
  } else {
    _autoInit();
  }

})(window);
/* END BL-005 AGENT E ONBOARDING */
